import { ICONS, preloadSvgAssets, replaceConversationCircleSVGs, replaceMiniPanelSVGs } from "./svg/assets.js";
import "./growthstack-menu.js";

console.log("[GHL Build] Theme JS loaded");

window.GHLTheme = {
  version: "0.1.2",
  scopeSelector: "._LCksl7nZ6vXELfyfu32G"
};

preloadSvgAssets();

const redirectAskAiRouteToDashboard = () => {
  const match = window.location.pathname.match(/^\/v2\/location\/([^/]+)\/ask-ai\/?$/);

  if (!match) return;

  window.location.replace(`/v2/location/${match[1]}/dashboard`);
};

redirectAskAiRouteToDashboard();
window.addEventListener("ghl-theme:route-change", redirectAskAiRouteToDashboard);

/**
 * GHL is a SPA, so URL changes often happen through history.pushState/replaceState
 * instead of a full page load. A lot of the white-label DOM work below was originally
 * polling for the right DOM. This lifecycle helper gives those same fixes explicit SPA
 * hooks:
 *
 * - patches pushState / replaceState once
 * - listens for popstate + hashchange
 * - emits "ghl-theme:route-change" and "locationchange"
 * - batches DOM-mutation-triggered reruns
 * - lets individual DOM fixes register as idempotent tasks
 */
const GHLThemeLifecycle = (() => {
  if (window.__ghlThemeLifecycle) return window.__ghlThemeLifecycle;

  const tasks = new Map();
  let lastHref = window.location.href;
  let scheduledTimer = null;
  let bodyObserver = null;

  const getRouteDetail = (source = "unknown") => {
    const previousHref = lastHref;
    const currentHref = window.location.href;

    return {
      source,
      previousHref,
      currentHref,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    };
  };

  const runTask = (task, reason) => {
    const now = Date.now();
    const minIntervalMs = Number.isFinite(task.minIntervalMs) ? task.minIntervalMs : 50;

    if (now - task.lastRun < minIntervalMs) return;
    task.lastRun = now;

    try {
      task.fn(reason);
    } catch (error) {
      console.warn(`[GHL Build] SPA lifecycle task failed: ${task.name}`, error);
    }
  };

  const runAll = reason => {
    tasks.forEach(task => runTask(task, reason));
  };

  const scheduleAll = (reason = "scheduled", delay = 75) => {
    clearTimeout(scheduledTimer);
    scheduledTimer = setTimeout(() => {
      window.requestAnimationFrame(() => runAll(reason));
    }, delay);
  };

  const notifyRouteChange = (source = "history") => {
    const detail = getRouteDetail(source);
    const hrefChanged = detail.currentHref !== lastHref;

    if (hrefChanged) {
      lastHref = detail.currentHref;

      window.dispatchEvent(new CustomEvent("ghl-theme:route-change", { detail }));
      window.dispatchEvent(new Event("locationchange"));

      scheduleAll(`route:${source}`, 0);
      scheduleAll(`route:${source}:after-dom`, 250);
      scheduleAll(`route:${source}:late`, 1000);
    } else {
      scheduleAll(`route:${source}:same-url`, 0);
    }
  };

  const patchHistoryMethod = type => {
    if (!window.history || !window.history[type]) return;

    const original = window.history[type];
    if (original.__ghlThemePatched) return;

    const patched = function () {
      const result = original.apply(this, arguments);
      notifyRouteChange(type);
      return result;
    };

    patched.__ghlThemePatched = true;
    patched.__ghlThemeOriginal = original;
    window.history[type] = patched;
  };

  patchHistoryMethod("pushState");
  patchHistoryMethod("replaceState");

  window.addEventListener("popstate", () => notifyRouteChange("popstate"));
  window.addEventListener("hashchange", () => notifyRouteChange("hashchange"));
  window.addEventListener("ghl-theme:navigate", () => scheduleAll("custom-navigate", 0));

  const startDomObserver = () => {
    if (bodyObserver || !document.body) return;

    bodyObserver = new MutationObserver(() => {
      scheduleAll("dom-mutation", 100);
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      startDomObserver();
      scheduleAll("dom-ready", 0);
    });
  } else {
    startDomObserver();
    scheduleAll("initial", 0);
  }

  // Safety net for framework navigations that bypass patched history.
  window.setInterval(() => {
    if (window.location.href !== lastHref) {
      notifyRouteChange("href-watch");
    }
  }, 500);

  const api = {
    register(name, fn, options = {}) {
      if (!name || typeof fn !== "function") return () => {};

      const task = {
        name,
        fn,
        lastRun: 0,
        minIntervalMs: options.minIntervalMs ?? 50
      };

      tasks.set(name, task);

      if (options.intervalMs) {
        task.intervalId = window.setInterval(() => runTask(task, "interval"), options.intervalMs);
      }

      if (options.runNow !== false) {
        scheduleAll(`register:${name}`, 0);
      }

      return () => {
        if (task.intervalId) window.clearInterval(task.intervalId);
        tasks.delete(name);
      };
    },

    schedule(reason = "manual", delay = 0) {
      scheduleAll(reason, delay);
    },

    notifyRouteChange
  };

  window.__ghlThemeLifecycle = api;
  return api;
})();

const registerSpaTask = (name, fn, options = {}) => GHLThemeLifecycle.register(name, fn, options);

registerSpaTask("replace-highrise-blue-text", () => {
  document.querySelectorAll(".crm-highrise .text-\\[\\#2970FF\\]").forEach(element => {
    element.style.setProperty("color", "var(--Dark-Purple)", "important");
  });
});

registerSpaTask("membership-transparent-background", () => {
  if (!window.location.pathname.includes("/memberships")) return;

  document.querySelectorAll("#membership, #membership > div > div > div").forEach(element => {
    element.style.setProperty("background", "transparent", "important");
  });
});

const isPaymentsIntegrationsDashboardRoute = () => window.location.pathname.includes("/payments/integrations/dashboard");

const isCustomSettingsPageRoute = () => {
  const href = window.location.href;

  const exclusions = [
    "affiliate-manager",
    "/memberships/courses/settings",
    "/automation/workflows/settings",
    "/settings/smart_list/Settings",
    "funnels-websites/client-portal",
    "/conversation-ai/",
    "conversations",
    "/memberships/client-portal/",
    "/funnels-websites/funnels/",
    "/reputation/settings",
    "/marketing/brand-boards",
    "/receipts",
    "/payments",
    "/marketing/social-planner",
    "/automation/campaigns"
  ];

  return (
    isPaymentsIntegrationsDashboardRoute() ||
    (href.includes("settings") && !exclusions.some(path => href.includes(path)))
  );
};

const rewriteMetricValue = el => {
  if (!el) return;

  const value = el.textContent.trim();
  el.setAttribute("data-value", value);

  if (!el.querySelector("span")) {
    const span = document.createElement("span");
    span.style.display = "none";
    span.textContent = value;
    el.textContent = "";
    el.appendChild(span);
  }
};

const wrapMetricTitle = ({ selector, wrapperClass, imgSrc, imgAlt, parentSelector }) => {
  document.querySelectorAll(`${selector}:not(.lead-title-processed)`).forEach(el => {
    el.classList.add("lead-title-processed");

    const wrapper = document.createElement("div");
    wrapper.className = wrapperClass;

    const img = document.createElement("img");
    img.src = imgSrc;
    img.alt = imgAlt;

    if (parentSelector) {
      const parent = el.closest(parentSelector);
      if (!parent) return;

      parent.insertBefore(wrapper, el);
    } else {
      el.insertAdjacentElement("beforebegin", wrapper);
    }

    wrapper.appendChild(img);
    wrapper.appendChild(el);
  });
};

const runRouteAwareDomFixes = () => {
  const body = document.body;
  if (!body) return;

  const pathname = window.location.pathname;
  const href = window.location.href;

  if (!pathname.includes("/settings/company")) {
    const searchContainer = document.querySelector(".flex.mb-4.h-7.flex-row");
    const switcherDiv = document.getElementById("location-switcher-sidbar-v2");

    if (searchContainer && switcherDiv && !document.querySelector(".search-dropdown-sidebar")) {
      const wrapper = document.createElement("div");
      wrapper.className = "search-dropdown-sidebar";

      searchContainer.parentNode.insertBefore(wrapper, searchContainer);
      wrapper.appendChild(switcherDiv);
      wrapper.appendChild(searchContainer);
    }

    const searchInput = document.getElementById("globalSearchOpener");
    const targetId = document.getElementById("search-bar-inbox");

    if (searchInput && targetId && !targetId.contains(searchInput)) {
      targetId.appendChild(searchInput);
    }

    const headerNav = document.querySelector(".hl_nav-header nav");
    const settings = document.querySelector(".hl_nav-settings");

    if (headerNav && settings && !headerNav.contains(settings)) {
      headerNav.appendChild(settings);
    }

    const dashboardTitle = document.querySelector(".location-dashboard_header .hl-title-container .title");
    if (dashboardTitle && dashboardTitle.textContent.trim() === "Dashboard") {
      dashboardTitle.textContent = "Leads Dashboard";
    }

    // const editBtn = document.querySelector("#location-dashboard_btn--edit-dashboard");
    // const editTarget = document.querySelector(".flex.items-center.flex-wrap.gap-2");
    // if (editBtn && editTarget && !editTarget.contains(editBtn)) {
    //   editTarget.appendChild(editBtn);
    // }
  }

  wrapMetricTitle({
    selector: ".dashboard-widget-type-horizontal_bar.dashboard-widget-module-crm-opportunities .hl-display-sm-semibold",
    wrapperClass: "lead-status-title",
    imgSrc: "https://influx-ghl.s3.us-west-2.amazonaws.com/lead-status-icon.png",
    imgAlt: "Lead Revenue Icon"
  });

  wrapMetricTitle({
    selector: ".crm-opportunities-value .hl-display-sm-semibold",
    wrapperClass: "lead-status-title",
    imgSrc: "https://influx-ghl.s3.us-west-2.amazonaws.com/lead-value-icon.png",
    imgAlt: "Lead Revenue Icon",
    parentSelector: ".w-full.mb-2"
  });

  wrapMetricTitle({
    selector: ".crm-opportunities-conversion-rate .hl-display-sm-semibold",
    wrapperClass: "lead-status-title",
    imgSrc: "https://influx-ghl.s3.us-west-2.amazonaws.com/lead-revenue-icon.png",
    imgAlt: "Lead Revenue Icon",
    parentSelector: ".w-full"
  });

  document
    .querySelectorAll(
      [
        ".dashboard-widget-type-horizontal_bar.dashboard-widget-module-crm-opportunities .hl-display-sm-semibold",
        ".crm-opportunities-value .hl-display-sm-semibold",
        ".crm-opportunities-conversion-rate .hl-display-sm-semibold",
        ".location-dashboard-lead-source-report .hl-display-sm-semibold"
      ].join(", ")
    )
    .forEach(rewriteMetricValue);

  const listSearch = document.getElementById("list-view-record-search");
  if (listSearch && listSearch.placeholder !== "Search Lead") {
    listSearch.placeholder = "Search Lead";
  }

  const topBar = document.querySelector("#newTopBar");
  const count = topBar && topBar.querySelector(".count");
  if (count) {
    count.childNodes.forEach(node => {
      if (node.nodeType === 3 && node.nodeValue.includes("opportunit")) {
        node.nodeValue = node.nodeValue.replace(/opportunit(?:y|ies)/gi, "Leads");
      }
    });
  }

  const buttonContentSpan = document.querySelector("#add-record-btn .hr-button__content");
  if (buttonContentSpan) {
    buttonContentSpan.childNodes.forEach(node => {
      if (node.nodeType === 3 && node.nodeValue.includes("Add opportunity")) {
        node.nodeValue = node.nodeValue.replace("Add opportunity", "Add Lead");
      }
    });
  }

  if (href.includes("/conversations/conversations/")) {
    body.classList.add("conversation-page");
  } else {
    body.classList.remove("conversation-page");
  }

  if (isCustomSettingsPageRoute()) {
    body.classList.add("is-settings-page-custom");
  } else {
    body.classList.remove("is-settings-page-custom");
  }

  if (isPaymentsIntegrationsDashboardRoute()) {
    body.classList.add("is-payments-integrations-dashboard");
  } else {
    body.classList.remove("is-payments-integrations-dashboard");
  }

  if (document.querySelector("#listInvoices")) {
    body.classList.add("list-invoices-page");
  } else {
    body.classList.remove("list-invoices-page");
  }

  document.querySelectorAll(".lead-connector a.active").forEach(el => el.classList.remove("active"));

  document.querySelectorAll('#app[style*="100vh"]').forEach(node => {
    node.style.setProperty("overflow", "visible", "important");
    node.style.setProperty("height", "auto", "important");
    node.style.setProperty("min-height", "100vh", "important");
    node.style.setProperty("padding-top", "0px", "important");
  });

  try {
    replaceConversationCircleSVGs();
    replaceMiniPanelSVGs();
  } catch (error) {
    console.warn("[GHL Build] SVG replacement failed", error);
  }
};

registerSpaTask("route-aware-dom-fixes", runRouteAwareDomFixes, {
  intervalMs: 1000,
  minIntervalMs: 75
});

//new change
if (!window.location.pathname.includes("/settings/company")) {
  const interval = setInterval(() => {
    const searchContainer = document.querySelector(".flex.mb-4.h-7.flex-row");
    const switcherDiv = document.getElementById("location-switcher-sidbar-v2");

    if (searchContainer && switcherDiv && !document.querySelector(".search-dropdown-sidebar")) {
      const wrapper = document.createElement("div");
      wrapper.className = "search-dropdown-sidebar";

      searchContainer.parentNode.insertBefore(wrapper, searchContainer);
      wrapper.appendChild(switcherDiv);
      wrapper.appendChild(searchContainer);

      clearInterval(interval);
    }
  }, 300);

  setInterval(() => {
    const searchInput = document.getElementById("globalSearchOpener");
    const targetId = document.getElementById("search-bar-inbox");

    if (searchInput && targetId) {
      if (!targetId.contains(searchInput)) {
        targetId.appendChild(searchInput);
      }
    }
  }, 300);

  const moveInterval = setInterval(() => {
    const headerNav = document.querySelector(".hl_nav-header nav");
    const settings = document.querySelector(".hl_nav-settings");

    if (headerNav && settings) {
      headerNav.appendChild(settings);
      clearInterval(moveInterval);
    }
  }, 150);

  const renameDashboardHeader = setInterval(() => {
    const title = document.querySelector(".location-dashboard_header .hl-title-container .title");
    if (title && title.textContent.trim() === "Dashboard") {
      title.textContent = "Leads Dashboard";
      clearInterval(renameDashboardHeader);
    }
  }, 100);

  // const moveEditBtn = setInterval(() => {
  //   const editBtn = document.querySelector("#location-dashboard_btn--edit-dashboard");
  //   const target = document.querySelector(".flex.items-center.flex-wrap.gap-2");
  //   if (editBtn && target && !target.contains(editBtn)) {
  //     target.appendChild(editBtn);
  //     clearInterval(moveEditBtn);
  //   }
  // }, 100);

  setInterval(() => {
    document.querySelectorAll(".dashboard-widget-type-horizontal_bar.dashboard-widget-module-crm-opportunities .hl-display-sm-semibold:not(.lead-title-processed)").forEach(el => {
      el.classList.add("lead-title-processed");

      const wrapper = document.createElement("div");
      wrapper.className = "lead-status-title";

      const img = document.createElement("img");
      img.src = "https://influx-ghl.s3.us-west-2.amazonaws.com/lead-status-icon.png";
      img.alt = "Lead Revenue Icon";

      el.insertAdjacentElement("beforebegin", wrapper);
      wrapper.appendChild(img);
      wrapper.appendChild(el);
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".dashboard-widget-type-horizontal_bar.dashboard-widget-module-crm-opportunities .hl-display-sm-semibold").forEach(el => {
      el.setAttribute("data-value", el.textContent.trim());
      if (!el.querySelector("span")) {
        const span = document.createElement("span");
        span.style.display = "none";
        span.textContent = el.textContent.trim();
        el.textContent = "";
        el.appendChild(span);
      }
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".crm-opportunities-value .hl-display-sm-semibold:not(.lead-title-processed)").forEach(el => {
      el.classList.add("lead-title-processed");

      const wrapper = document.createElement("div");
      wrapper.className = "lead-status-title";

      const img = document.createElement("img");
      img.src = "https://influx-ghl.s3.us-west-2.amazonaws.com/lead-value-icon.png";
      img.alt = "Lead Revenue Icon";

      const parent = el.closest(".w-full.mb-2");
      if (parent) {
        parent.insertBefore(wrapper, el);
        wrapper.appendChild(img);
        wrapper.appendChild(el);
      }
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".crm-opportunities-value .hl-display-sm-semibold").forEach(el => {
      el.setAttribute("data-value", el.textContent.trim());
      if (!el.querySelector("span")) {
        const span = document.createElement("span");
        span.style.display = "none";
        span.textContent = el.textContent.trim();
        el.textContent = "";
        el.appendChild(span);
      }
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".crm-opportunities-conversion-rate .hl-display-sm-semibold:not(.lead-title-processed)").forEach(el => {
      el.classList.add("lead-title-processed");

      const wrapper = document.createElement("div");
      wrapper.className = "lead-status-title";

      const img = document.createElement("img");
      img.src = "https://influx-ghl.s3.us-west-2.amazonaws.com/lead-revenue-icon.png";
      img.alt = "Lead Revenue Icon";

      const parent = el.closest(".w-full");
      if (parent) {
        parent.insertBefore(wrapper, el);
        wrapper.appendChild(img);
        wrapper.appendChild(el);
      }
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".crm-opportunities-conversion-rate .hl-display-sm-semibold").forEach(el => {
      el.setAttribute("data-value", el.textContent.trim());
      if (!el.querySelector("span")) {
        const span = document.createElement("span");
        span.style.display = "none";
        span.textContent = el.textContent.trim();
        el.textContent = "";
        el.appendChild(span);
      }
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".location-dashboard-lead-source-report .hl-display-sm-semibold").forEach(el => {
      el.setAttribute("data-value", el.textContent.trim());

      if (!el.querySelector("span")) {
        const span = document.createElement("span");
        span.style.display = "none";
        span.textContent = el.textContent.trim();
        el.textContent = "";
        el.appendChild(span);
      }
    });
  }, 300);

  // setInterval(() => {
  //   document.querySelectorAll(".ui-card-content *").forEach((el) => {
  //     if (
  //       el.childNodes.length === 1 &&
  //       el.textContent.match(/Opportunity|Source|Value/i)
  //     ) {
  //       el.textContent = el.textContent
  //         .replace(/Opportunity\s*:?\s*/gi, "")
  //         .replace(/Source\s*:?\s*/gi, "")
  //         .replace(/Value\s*:?\s*/gi, "");
  //     }
  //   });
  // }, 100);

  // const pollInterval = setInterval(() => {
  //   const allCards = document.querySelectorAll(".ui-card-content");

  //   if (allCards.length === 0) {
  //     return;
  //   }

  //   allCards.forEach((card) => {
  //     const headerDiv = card.querySelector("div[data-contact-id]");

  //     if (!headerDiv || headerDiv.hasAttribute("data-elements-moved")) {
  //       return;
  //     }

  //     const originalAvatar = headerDiv.querySelector(".hr-avatar");
  //     const priceElement = headerDiv.nextElementSibling;

  //     if (
  //       originalAvatar &&
  //       priceElement &&
  //       priceElement.textContent.includes("$")
  //     ) {
  //       const rawText = priceElement.textContent.trim();
  //       const parentWrapper = document.createElement("span");
  //       parentWrapper.className = "lead-source-price-wrapper";

  //       const match = rawText.match(/(.*)(\$.*)/);

  //       if (match && match[1].trim().length > 0 && match[2]) {
  //         const sourceText = match[1];
  //         const valueText = match[2];

  //         const sourceSpan = document.createElement("span");
  //         sourceSpan.className = "lead-source";
  //         sourceSpan.textContent = sourceText;

  //         const pipeSpan = document.createElement("span");
  //         pipeSpan.className = "lead-divider";
  //         pipeSpan.textContent = "|";

  //         const valueSpan = document.createElement("span");
  //         valueSpan.className = "lead-price";
  //         valueSpan.textContent = valueText;

  //         parentWrapper.appendChild(sourceSpan);
  //         parentWrapper.appendChild(pipeSpan);
  //         parentWrapper.appendChild(valueSpan);
  //       } else {
  //         const valueSpan = document.createElement("span");
  //         valueSpan.className = "lead-price";
  //         valueSpan.textContent = rawText;
  //         parentWrapper.appendChild(valueSpan);
  //       }

  //       const newDiv = document.createElement("div");
  //       newDiv.className = "image-lead-origin-flex";

  //       newDiv.appendChild(originalAvatar);
  //       newDiv.appendChild(parentWrapper);

  //       headerDiv.after(newDiv);
  //       priceElement.style.display = "none";
  //       headerDiv.setAttribute("data-elements-moved", "true");
  //     }
  //   });
  // }, 300);

  setInterval(function () {
    const searchInput = document.getElementById("list-view-record-search");
    if (searchInput) {
      searchInput.placeholder = "Search Lead";
    }
  }, 300);

  setInterval(() => {
    (() => {
      const parent = document.querySelector("#newTopBar");
      if (!parent) return;

      const count = parent.querySelector(".count");
      if (!count) return;

      count.childNodes.forEach(node => {
        if (node.nodeType === 3 && node.nodeValue.includes("opportunit")) {
          node.nodeValue = node.nodeValue.replace(/opportunit(?:y|ies)/gi, "Leads");
        }
      });
    })();
  }, 300);

  const renameAddButtonInterval = setInterval(() => {
    const buttonContentSpan = document.querySelector("#add-record-btn .hr-button__content");

    if (buttonContentSpan) {
      let textNodeFound = false;

      buttonContentSpan.childNodes.forEach(node => {
        if (node.nodeType === 3 && node.nodeValue.includes("Add opportunity")) {
          node.nodeValue = node.nodeValue.replace("Add opportunity", "Add Lead");
          textNodeFound = true;
        }
      });

      if (textNodeFound) {
        clearInterval(renameAddButtonInterval);
      }
    }
  }, 50);

(function () {
  let moveDashboardHeaderInterval;
  let observer;

  function isDashboardRoute() {
    return (
      window.location.pathname.includes("/dashboard") &&
      !window.location.pathname.includes("membership") &&
      !window.location.pathname.includes("funnel") &&
      !window.location.pathname.includes("wordpress")
    );
  }

  function getFreshDashboardHeader() {
    const headers = Array.from(document.querySelectorAll(".location-dashboard_header"));

    return (
      headers.find(header => !header.closest("header.hl_header")) ||
      headers.find(header => header.querySelector("#location-dashboard_btn--save-dashboard")) ||
      headers.find(header => header.querySelector("#location-dashboard_btn--cancel-dashboard")) ||
      headers.find(header => header.querySelector("#location-dashboard_btn--edit-dashboard")) ||
      null
    );
  }

  function renameDashboardTitle(header) {
    const title = header?.querySelector(".hl-header-content .hl-title-container p.title, .hl-title-container .title");

    if (title && title.textContent.trim() === "Dashboard") {
      title.textContent = "Leads Dashboard";
    }
  }

  function syncDashboardHeader() {
    const targetHeader = document.querySelector("header.hl_header .container-fluid");
    const movedHeader = document.querySelector("header.hl_header .location-dashboard_header");

    if (!isDashboardRoute()) {
      document.body.classList.remove("main-dashboard");

      if (movedHeader) {
        movedHeader.remove();
      }

      return;
    }

    document.body.classList.add("main-dashboard");

    if (!targetHeader) return;

    const freshHeader = getFreshDashboardHeader();

    if (freshHeader && freshHeader !== movedHeader) {
      if (movedHeader) movedHeader.remove();

      targetHeader.prepend(freshHeader);
      renameDashboardTitle(freshHeader);
      return;
    }

    if (movedHeader) {
      renameDashboardTitle(movedHeader);
    }
  }

  function startDashboardHeaderSync() {
    if (moveDashboardHeaderInterval) clearInterval(moveDashboardHeaderInterval);

    moveDashboardHeaderInterval = setInterval(syncDashboardHeader, 300);
    syncDashboardHeader();
  }

  startDashboardHeaderSync();

  observer = new MutationObserver(syncDashboardHeader);
  observer.observe(document.body, { childList: true, subtree: true });

  window.addEventListener("locationchange", syncDashboardHeader);
  window.addEventListener("popstate", syncDashboardHeader);
  window.addEventListener("hashchange", syncDashboardHeader);
})();

  const conversationCircleObserver = new MutationObserver(() => {
    replaceConversationCircleSVGs();
  });

  conversationCircleObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  setInterval(() => {
    replaceMiniPanelSVGs();
  }, 300);

  function getCurrentTopMenu(header) {
    const topMenus = Array.from(document.querySelectorAll("div.topmenu-nav"));

    if (window.location.pathname.includes("/tasks")) {
      return topMenus.find(topMenu => !header.contains(topMenu)) || topMenus[0];
    }

    return topMenus[0];
  }

  function moveCurrentTopMenu(leftDiv, topMenu) {
    if (!leftDiv || !topMenu) return;

    if (window.location.pathname.includes("/tasks")) {
      leftDiv.querySelectorAll(":scope > .topmenu-nav").forEach(currentTopMenu => {
        if (currentTopMenu !== topMenu) currentTopMenu.remove();
      });
    }

    if (!leftDiv.contains(topMenu)) {
      leftDiv.appendChild(topMenu);
    }
  }

  function moveHeaderElements() {
    if (document.body.classList.contains("is-settings-page-custom")) return;
    if (
      window.location.pathname.includes("/dashboard") &&
      !window.location.pathname.includes("membership") &&
      !window.location.pathname.includes("wordpress")
      // !window.location.pathname.includes("funnel")
    ) {
      return;
    }

    // if (window.location.pathname.includes("settings")) {
    //   return;
    // }

    const header = document.querySelector("header.hl_header");
    const topMenu = header && getCurrentTopMenu(header);

    if (!header || !topMenu) {
      return;
    }

    let wrapper = header.querySelector(".hl-top-wrapper");

    if (!wrapper) {
      wrapper = document.createElement("div");
      wrapper.className = "hl-top-wrapper";

      const leftDiv = document.createElement("div");
      leftDiv.className = "hl-top-left";

      const rightDiv = document.createElement("div");
      rightDiv.className = "hl-top-right";

      while (header.firstChild) {
        rightDiv.appendChild(header.firstChild);
      }

      wrapper.appendChild(leftDiv);
      wrapper.appendChild(rightDiv);
      header.appendChild(wrapper);
    }

    const leftDiv = wrapper.querySelector(".hl-top-left");
    moveCurrentTopMenu(leftDiv, topMenu);
  }

  setInterval(moveHeaderElements, 300);

  function toggleDashboardBodyClass() {
    const body = document.body;

    if (
      window.location.pathname.includes("/dashboard") &&
      !window.location.pathname.includes("membership") &&
      !window.location.pathname.includes("wordpress") &&
      !window.location.pathname.includes("funnel")
    ) {
      body.classList.add("main-dashboard");
    } else {
      body.classList.remove("main-dashboard");
    }
  }

  setInterval(toggleDashboardBodyClass, 300);

  function updateHeaderLayout() {
    const body = document.body;
    if (document.body.classList.contains("is-settings-page-custom")) return;
    if (
      window.location.pathname.includes("/dashboard") &&
      !window.location.pathname.includes("membership") &&
      !window.location.pathname.includes("wordpress")
      // !window.location.pathname.includes("funnel")
    ) {
      body.classList.add("main-dashboard");
      return;
    } else {
      body.classList.remove("main-dashboard");
    }

    // if (window.location.pathname.includes("settings")) {
    //   return;
    // }

    const header = document.querySelector("header.hl_header");
    const topMenu = header && getCurrentTopMenu(header);

    if (!header || !topMenu) return;

    let wrapper = header.querySelector(".hl-top-wrapper");
    let leftDiv;

    if (!wrapper) {
      wrapper = document.createElement("div");
      wrapper.className = "hl-top-wrapper";
      leftDiv = document.createElement("div");
      leftDiv.className = "hl-top-left";
      const rightDiv = document.createElement("div");
      rightDiv.className = "hl-top-right";
      while (header.firstChild) {
        rightDiv.appendChild(header.firstChild);
      }
      wrapper.appendChild(leftDiv);
      wrapper.appendChild(rightDiv);
      header.appendChild(wrapper);
    }

    leftDiv = header.querySelector(".hl-top-left");
    moveCurrentTopMenu(leftDiv, topMenu);

    let iconContainer = document.getElementById("hl-active-icon-container");
    if (!iconContainer) {
      iconContainer = document.createElement("div");
      iconContainer.id = "hl-active-icon-container";
      wrapper.insertBefore(iconContainer, leftDiv);
    }

    const activeSubLink = document.querySelector(".side-bar-custom-link.active-sub-link");
    const activeCategoryLink = document.querySelector(".side-bar-custom-link.background-highlight");
    const contactsLink = window.location.pathname.includes("/contacts") ? document.getElementById("sb_contacts") : null;

    const targetLink = contactsLink || activeSubLink || activeCategoryLink;

    let foundSVG = null;
    if (targetLink) {
      foundSVG = targetLink.querySelector("svg");
    }

    if (window.location.pathname.includes("/tasks")) {
      iconContainer.innerHTML = `
    <div class="icon-custom-header-wrapper">
      ${ICONS.REMINDERS}
    </div>
  `;
      
      return;
    }

    if (window.location.pathname.includes("/contacts")) {
      if (!ICONS.CONTACTS) return;

      if (iconContainer.dataset.currentIconKey !== "contacts") {
        iconContainer.innerHTML = `
          <div class="icon-custom-header-wrapper">
            ${ICONS.CONTACTS}
          </div>
        `;
        iconContainer.dataset.currentIconKey = "contacts";
      }

      return;
    }

    const newIconKey = foundSVG ? "svg-" + targetLink.getAttribute("href") : "none";

    if (iconContainer.dataset.currentIconKey !== newIconKey) {
      iconContainer.innerHTML = "";
      if (foundSVG) {
        const iconWrapper = document.createElement("div");
        iconWrapper.className = "icon-custom-header-wrapper";

        const clonedSVG = foundSVG.cloneNode(true);

        const paths = clonedSVG.querySelectorAll("path");
        paths.forEach(path => {
          if (path.getAttribute("stroke") && path.getAttribute("stroke").startsWith("url")) {
            path.setAttribute("stroke", "#707073");
          }
          if (path.getAttribute("fill") && path.getAttribute("fill").startsWith("url")) {
            path.setAttribute("fill", "#707073");
          }
        });

        iconWrapper.appendChild(clonedSVG);
        iconContainer.appendChild(iconWrapper);
      }
      iconContainer.dataset.currentIconKey = newIconKey;
    }
  }

  setInterval(updateHeaderLayout, 300);

  setInterval(() => {
    document.querySelectorAll(".ui-card-content").forEach(card => {
      const imageFlex = card.querySelector(".image-lead-origin-flex");
      const dateDiv = card.querySelector(".hr-tag.ui-primary");

      if (imageFlex && dateDiv && !card.querySelector(".has-date-option")) {
        const wrapper = document.createElement("div");
        wrapper.className = "has-date-option";
        dateDiv.parentElement.remove();
        wrapper.appendChild(dateDiv);
        imageFlex.insertAdjacentElement("afterend", wrapper);
      }
    });
  }, 300);

  (function () {
    const OPEN_CHEVRON_PATH = "M11.8887 9.94434L7.99978 6.05545L4.11089 9.94434";
    const menusToClose = ["Team Inbox", "My Inbox"];
    const CUSTOM_CLASS = "flex-buttons-left-conversation-panel";

    function closeMenus() {
      menusToClose.forEach(text => {
        const span = document.evaluate(`//span[contains(., '${text}')]`, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
        if (!span) return;
        const parent = span.closest(".cursor-pointer");
        const open = parent?.querySelector(`path[d="${OPEN_CHEVRON_PATH}"]`);
        if (open) parent.click();
      });
    }

    function addClass() {
      const span = document.evaluate("//span[contains(., 'Team Inbox')]", document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
      const container = span?.closest(".space-y-2")?.parentElement;
      if (container && !container.classList.contains(CUSTOM_CLASS)) container.classList.add(CUSTOM_CLASS);
    }

    function run() {
      closeMenus();
      addClass();
    }

    let hasRun = false;

    const observer = new MutationObserver(() => {
      const teamInbox = document.evaluate("//span[contains(., 'Team Inbox')]", document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
      if (teamInbox && !hasRun) {
        run();
        hasRun = true;
      } else if (!teamInbox) {
        hasRun = false;
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  })();

  setInterval(() => {
    document.querySelectorAll(".hl_conversations--messages-list-v2-left-most-panel section section").forEach(el => {
      el.style.removeProperty("padding");
      el.style.setProperty("padding", "0px", "important");
    });
  }, 300);

  setInterval(() => {
    const sidebar = document.querySelector("#sidebar-v2");
    if (sidebar && (document.querySelector(".hl_conversations--messages-list-v2-left-most-panel") || document.querySelector(".hl_contact-details-new--wrap"))) {
      sidebar.style.paddingRight = "5px";
      sidebar.style.marginRight = "0";
    }
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".hl-card-header > div > div.truncate.mr-2 > p").forEach(p => {
      const title = p.textContent.trim();
      if (title === "Leads") p.classList.add("card-title-leads");
      if (title === "Consult") p.classList.add("card-title-consult");
      if (title === "Booked") p.classList.add("card-title-booked");
      if (title === "Completed") p.classList.add("card-title-completed");
      if (title === "Cold") p.classList.add("card-title-grey");
      if (title === "Lost") p.classList.add("card-title-grey");
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".crm-opportunities-funnel .hl-card-header .truncate.mr-2 p.hl-text-md-medium").forEach(p => {
      if (!p.classList.contains("funnel-icon-added")) {
        p.classList.add("funnel-icon-added");

        p.innerHTML = `
        <span class="funnel-icon"></span>
        ${p.innerHTML}
      `;
      }
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".location-dashboard-lead-source-report .hl-card-header .hl-text-md-medium").forEach(p => {
      if (!p.classList.contains("lead-src-icon-added")) {
        p.classList.add("lead-src-icon-added");

        p.innerHTML = `
        <span class="lead-src-icon"></span>
        ${p.innerHTML}
      `;
      }
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll('div[role="group"][aria-label="Facebook Ads Report"] .hl-card-header .hl-text-md-medium').forEach(p => {
      if (!p.classList.contains("fb-ads-icon-added")) {
        p.classList.add("fb-ads-icon-added");

        p.innerHTML = `
        <span class="fb-ads-icon"></span>
        ${p.innerHTML}
      `;
      }
    });
  }, 300);

  setInterval(() => {
    document.querySelectorAll('div[role="group"][aria-label="Google Ads Report"] .hl-card-header .hl-text-md-medium').forEach(p => {
      if (!p.classList.contains("google-ads-icon-added")) {
        p.classList.add("google-ads-icon-added");

        p.innerHTML = `
        <span class="google-ads-icon"></span>
        ${p.innerHTML}
      `;
      }
    });
  }, 300);

  setInterval(() => {
    const title = document.querySelector('[aria-label="titleGmbReports"] .hl-card-header p.hl-text-md-medium');

    if (title && !title.classList.contains("gmb-title-icon-added")) {
      title.classList.add("gmb-title-icon-added");

      title.innerHTML = `
      <span class="gmb-title-icon"></span>
      ${title.textContent.trim()}
    `;
    }
  }, 300);

  setInterval(() => {
    const title = document.querySelector('[aria-label="titleGoogleAnalyticsCount"] .hl-card-header p.hl-text-md-medium');

    if (title && !title.classList.contains("ga-title-icon-added")) {
      title.classList.add("ga-title-icon-added");

      title.innerHTML = `
      <span class="ga-title-icon"></span>
      ${title.textContent.trim()}
    `;
    }
  }, 300);

  setInterval(() => {
    const title = document.querySelector('[aria-label="titleGoogleAnalyticsChart"] .hl-card-header p.hl-text-md-medium');

    if (title && !title.classList.contains("ga-chart-title-icon-added")) {
      title.classList.add("ga-chart-title-icon-added");

      title.innerHTML = `
      <span class="ga-chart-title-icon"></span>
      ${title.textContent.trim()}
    `;
    }
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".new-email-thread-view--collapsed-container").forEach(container => {
      const opened = container.querySelector(".bg-green-100");
      const box = container.querySelector(".email-view-container");

      if (!box) return;
      if (opened) {
        box.classList.add("email-opened");
      } else {
        box.classList.remove("email-opened");
      }
    });
  }, 300);

  var targetBody = document.body;
  var className = "ghl-internal-settings-page";

  if (targetBody) {
    var settingsObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === "attributes" && mutation.attributeName === "data-parent") {
          if (targetBody.getAttribute("data-parent") === "settings") {
            if (!targetBody.classList.contains(className)) {
              targetBody.classList.add(className);
            }
          } else {
            targetBody.classList.remove(className);
          }
        }
      });
    });

    settingsObserver.observe(targetBody, { attributes: true });

    if (targetBody.getAttribute("data-parent") === "settings") {
      targetBody.classList.add(className);
    }
  }

  setInterval(() => {
    if (document.body.classList.contains("list-invoices-page")) return;
    if (document.querySelector("#listInvoices")) {
      document.body.classList.add("list-invoices-page");
    }
  }, 300);

  setInterval(() => {
    document.querySelectorAll(".lead-connector a.active").forEach(el => el.classList.remove("active"));
  }, 300);

  const sidebarStateMonitor = new MutationObserver(changes => {
    changes.forEach(change => {
      if (change.attributeName === "class") {
        const sidebarContainer = document.querySelector(".sidebar-v2-location");
        const searchComponent = document.querySelector(".search-dropdown-sidebar");

        if (sidebarContainer && searchComponent) {
          if (sidebarContainer.classList.contains("v2-collapse")) {
            searchComponent.style.setProperty("display", "none", "important");
          } else {
            searchComponent.style.removeProperty("display");
          }
        }
      }
    });
  });

  const sidebarWrapper = document.querySelector(".sidebar-v2-location");
  if (sidebarWrapper) {
    sidebarStateMonitor.observe(sidebarWrapper, { attributes: true });

    if (sidebarWrapper.classList.contains("v2-collapse")) {
      const searchComponent = document.querySelector(".search-dropdown-sidebar");
      if (searchComponent) searchComponent.style.setProperty("display", "none", "important");
    }
  }

  setInterval(() => {
    const container = document.querySelector(".lead-connector");
    const navHeader = document.querySelector(".hl_nav-header");
    const existing = document.getElementById("custom-google-ads-link");

    if (container && navHeader && !existing) {
      const iconWrapper = document.createElement("a");
      iconWrapper.id = "custom-google-ads-link";
      iconWrapper.href = "https://platform.growthstack.app";
      iconWrapper.className = "flex justify-center mb-4 px-3 flex-shrink-0";

      const reportImg = document.createElement("img");
      reportImg.src = "https://influx-ghl.s3.us-west-2.amazonaws.com/power-by-influx-logo.png";
      reportImg.alt = "Powered by Influx Logo";
      reportImg.className = "influx-sidebar-logo";

      iconWrapper.appendChild(reportImg);
      navHeader.after(iconWrapper);
    }
  }, 300);

  setInterval(() => {
    const sidebar = document.querySelector(".sidebar-v2-location");
    const logo = document.querySelector(".agency-logo");

    if (sidebar && logo) {
      const isCollapsed = sidebar.classList.contains("v2-collapse");
      const targetSrc = isCollapsed
        ? "https://influx-ghl.s3.us-west-2.amazonaws.com/growth-stack-logo-icon.png"
        : "https://influx-ghl.s3.us-west-2.amazonaws.com/growth-stack-logo.png";

      // Only update if the source has actually changed
      if (logo.getAttribute("src") !== targetSrc) {
        logo.src = targetSrc;

        if (isCollapsed) {
          logo.classList.add("collapsed-agency-logo");
        } else {
          logo.classList.remove("collapsed-agency-logo");
        }
      }
    }
  }, 300);

  function renameNavItems() {
    const mappings = [
      {
        id: "tb_payment-invoices",
        old: "Invoices & Estimates",
        new: " Invoices "
      },
      {
        id: "tb_proposals-estimates",
        old: "Documents & Contracts",
        new: " Documents "
      },
      { id: "tb_payment-links", old: "Payment Links", new: " Payment " }
    ];

    mappings.forEach(item => {
      const el = document.getElementById(item.id);
      if (el) {
        const span = el.querySelector("span");
        if (span && span.innerText.includes(item.old)) {
          const textNode = Array.from(span.childNodes).find(node => node.nodeType === Node.TEXT_NODE && node.textContent.includes(item.old));
          if (textNode) textNode.textContent = item.new;
        }
      }
    });

    document.querySelectorAll(".topmenu-navitem, .custom-dropdown-item").forEach(link => {
      const href = link.getAttribute("href");
      if (href && window.location.pathname.includes(href)) {
        link.setAttribute("aria-current", "page");
      }
    });

    const moreIds = ["tb_payments-coupons", "tb_payment-settings", "tb_payment-integrations"];
    const navParent = document.querySelector(".topmenu-nav");

    if (!navParent || document.getElementById("tb_more-dropdown")) return;

    const itemsToMove = moreIds.map(id => document.getElementById(id)).filter(el => el !== null);

    // if (itemsToMove.length > 0) {
    //      const moreDropdown = document.createElement('div');
    //      moreDropdown.id = 'tb_more-dropdown';
    //      moreDropdown.className = 'group text-left mx-1 pb-2 md:pb-3 text-sm font-medium topmenu-navitem cursor-pointer relative px-2';

    //      moreDropdown.innerHTML = `
    //          <span class="flex items-center" style="line-height: 1 !important;">
    //              More <i class="ml-2 fas fa-angle-down"></i>
    //          </span>
    //          <div class="dropdown-content hidden group-hover:block origin-top-right absolute right-0 mt-0 w-max rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
    //              <ul class="list-none p-0 m-0"></ul>
    //          </div>
    //      `;

    //      const menuList = moreDropdown.querySelector('ul');

    //      itemsToMove.forEach(el => {
    //          const li = document.createElement('li');
    //          li.className = "block px-2 py-1 hover:bg-gray-100";

    //          el.classList.remove('mx-1', 'pb-2', 'md:pb-3', 'px-2');
    //          el.style.display = "block";
    //          el.style.padding = "8px 15px";

    //          li.appendChild(el);
    //          menuList.appendChild(li);
    //      });

    //      navParent.appendChild(moreDropdown);
    // }
  }

  setInterval(renameNavItems, 300);
}

(function () {
  const observer = new MutationObserver(() => {
    const path = window.location.pathname;
    if (path.includes("/settings/calendars")) {
      const body = document.body;
      if (body.getAttribute("data-name") === "company-settings-v2") {
        body.setAttribute("data-name", "calanders-company-settings-v2");
      }
    }
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["data-name"]
  });
})();

setInterval(() => {
  const parent = document.getElementById("globalSearchOpener");
  if (parent) {
    const el = parent.querySelector(".search-placeholder");
    if (el && el.innerHTML !== "Search") {
      el.innerHTML = "Search";
    }
  }
}, 300);

setInterval(() => {
  const isConvPage = window.location.href.includes("/conversations/conversations/");
  const bodyClass = document.body.classList;

  if (isConvPage && !bodyClass.contains("conversation-page")) {
    bodyClass.add("conversation-page");
  } else if (!isConvPage && bodyClass.contains("conversation-page")) {
    bodyClass.remove("conversation-page");
  }
}, 1000);

///SIDEBAR UPDATE
const enforcedStyles = "overflow: visible !important; height: auto !important; min-height: 100vh !important; padding-top: 0px !important;";

const applyForcedLayout = node => {
  if (node && node.getAttribute("style") !== enforcedStyles) {
    node.style.setProperty("overflow", "visible", "important");
    node.style.setProperty("height", "auto", "important");
    node.style.setProperty("min-height", "100vh", "important");
    node.style.setProperty("padding-top", "0px", "important");
  }
};

const layoutWatcher = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    if (mutation.type === "attributes" && mutation.attributeName === "style") {
      applyForcedLayout(mutation.target);
    }
  });
});

const executeStyleFix = () => {
  setTimeout(() => {
    const allLockedNodes = document.querySelectorAll('#app[style*="100vh"]');

    allLockedNodes.forEach(node => {
      applyForcedLayout(node);
      layoutWatcher.observe(node, { attributes: true });
    });
  }, 1000);
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", executeStyleFix);
} else {
  executeStyleFix();
}

// setInterval(() => {
//     const path = window.location.pathname;

//     if (path.includes('/opportunities/pipeline')) {
//         document.body.classList.add('pipeline-custom-view');
//     } else {
//         document.body.classList.remove('pipeline-custom-view');
//     }

//     if (path.includes('/settings/calendars/availability')) {
//         document.body.classList.add('calendar-availability-view');
//     } else {
//         document.body.classList.remove('calendar-availability-view');
//     }

//     if (path.includes('/settings/calendars/connections')) {
//         document.body.classList.add('calendar-connections-view');
//     } else {
//         document.body.classList.remove('calendar-connections-view');
//     }
// }, 300);

const injectName = () => {
  const nameSource = document.querySelector(".user-info-card .text-gray-900");
  const avatarWrapper = document.querySelector(".hl_header--avatar");

  if (nameSource && avatarWrapper) {
    if (!document.querySelector(".custom-name-container")) {
      const nameText = nameSource.innerText.trim();
      const nameDiv = document.createElement("div");

      nameDiv.innerText = nameText;
      nameDiv.classList.add("custom-name-container");
      nameDiv.style.cssText = `
                font-weight: 700;
                line-height: 1;
                font-size: 16px;
                margin-right: 15px;
                display: flex;
                align-items: center;
                color: white;
            `;

      avatarWrapper.parentNode.style.display = "flex";
      avatarWrapper.parentNode.style.alignItems = "center";
      avatarWrapper.parentNode.insertBefore(nameDiv, avatarWrapper);
    }
  } else {
    setTimeout(injectName, 300);
  }
};

injectName();

// const hideSingleLoc = setInterval(() => {
//   const singleLoc = document.querySelector('.single_location');
//   if (singleLoc) {
//     const parent = singleLoc.closest('.search-dropdown-sidebar');
//     if (parent) {
//       parent.style.setProperty('display', 'none', 'important');
//       clearInterval(hideSingleLoc);
//     }
//   }
// }, 500);

// New Sidebar
const preStyle = document.createElement("style");
preStyle.innerHTML = `
  .hl-top-left .topmenu-nav .topmenu-navitem:not(.custom-dropdown-item),
  .is-payments-integrations-dashboard .hl_header .container-fluid > .topmenu-nav .topmenu-navitem:not(.custom-dropdown-item) {
    display: none !important;
  }
`;
document.documentElement.appendChild(preStyle);

const initScript = () => {
  let lastActiveText = null;
  let lastActiveIcon = null;
  let lastMenuHtml = null;
  let domObserver;

  const isExternalTrackingPage = () => {
    const query = new URLSearchParams(window.location.search);

    return (
      (window.location.pathname.endsWith("/analytics") &&
        query.get("type") === "external_analytics") ||
      (window.location.pathname.endsWith("/form-builder/submissions") &&
        query.get("formType") === "external")
    );
  };

  const getActiveSidebarData = () => {
    let activeLink =
      document.querySelector(
        ".hl_nav-header nav a.exact-active, .hl_nav-header nav a.active, .hl_nav-header .active-sub-link, .hl_nav-header .background-highlight, #sidebar-v2 a.active"
      ) || document.querySelector("a.exact-active");

    if (!activeLink && window.location.pathname.includes("/reputation/")) {
      activeLink = document.getElementById("sb_reputation");
    }

    if (window.location.pathname.includes("/contacts")) {
      activeLink = document.getElementById("sb_contacts");
    }

    if (window.location.pathname.includes("/tasks")) {
      return {
        text: "Reminders",
        icon: `<div class="icon-custom-header-wrapper">${ICONS.REMINDERS}</div>`
      };
    }

    if (!activeLink) return { text: "Menu", icon: "" };

    const titleEl = activeLink.querySelector(".nav-title");
    const text = titleEl ? titleEl.innerText.trim() : activeLink.innerText.trim() || "Menu";

    const iconEl = activeLink.querySelector("svg, img, i");
    const icon = iconEl ? iconEl.outerHTML : "";

    return { text, icon };
  };

  const buildOrUpdateMenu = () => {
    const isPaymentsIntegrationsDashboard = isPaymentsIntegrationsDashboardRoute();

    if (isPaymentsIntegrationsDashboard) {
      const displacedTopMenu = document.querySelector("section.w-full > .topmenu-nav");
      const paymentHeaderContainer = document.querySelector(".hl_header .container-fluid");

      if (displacedTopMenu && paymentHeaderContainer && !paymentHeaderContainer.querySelector(":scope > .topmenu-nav")) {
        paymentHeaderContainer.prepend(displacedTopMenu);
      }
    }

    const navContainer = isPaymentsIntegrationsDashboard
      ? document.querySelector(".hl_header .container-fluid > .topmenu-nav")
      : document.querySelector(".hl-top-left .topmenu-nav");

    if (!navContainer) return;
    if (document.body.classList.contains("is-settings-page-custom") && !isPaymentsIntegrationsDashboard) return;

    const existingStaticWrapper = navContainer.querySelector(".custom-nav-wrapper");
    if (isPaymentsIntegrationsDashboard && existingStaticWrapper && !existingStaticWrapper.querySelector(".custom-dropdown-content")) {
      existingStaticWrapper.remove();
    }

    const originalLinks = Array.from(navContainer.children).filter(
      el =>
        (el.classList.contains("topmenu-navitem") || el.tagName === "A") &&
        !el.classList.contains("custom-nav-wrapper")
    );

    if ((isExternalTrackingPage() || isPaymentsIntegrationsDashboard) && originalLinks.length === 0) return;

    if (domObserver) domObserver.disconnect();

    if (!document.getElementById("custom-menu-styles")) {
      const style = document.createElement("style");
      style.id = "custom-menu-styles";
      style.innerHTML = `
    .hl-top-left .topmenu-nav > .topmenu-navitem:not(.custom-nav-wrapper),
    .is-payments-integrations-dashboard .hl_header .container-fluid > .topmenu-nav > .topmenu-navitem:not(.custom-nav-wrapper) { display: none !important; }
    .hl-top-left .topmenu-nav > .topmenu-navtitle,
    .is-payments-integrations-dashboard .hl_header .container-fluid > .topmenu-nav > .topmenu-navtitle { display: none !important; }
    .custom-nav-wrapper { display: flex; align-items: center; gap: 8px; position: relative; }
    .custom-menu-btn { display: flex; align-items: center; gap: 8px; background: transparent; border: none; color: white; font-weight: 700; font-size: 15px; cursor: pointer; padding: 0; }
    .custom-icon-wrapper { position: relative; display: flex; align-items: center; cursor: pointer; }
    
    .custom-icon-wrapper .custom-dropdown-content { 
        position: absolute !important;
        top: 0px !important; 
        left: 35px !important; 
        visibility: hidden; 
        opacity: 0; 
        pointer-events: none; 
        z-index: 9999; 
        background-color: white !important;
        box-shadow: 0 30px 40px 0 rgba(0, 0, 0, 0.07), 0 21.78px 33.422px 0 rgba(0, 0, 0, 0.04), 0 6.65px 5.32px 0 rgba(0, 0, 0, 0.03), 0 2.767px 2.214px 0 rgba(0, 0, 0, 0.02);
        border-radius: 15px; 
        padding: 8px 0; 
        min-width: 220px; 
        width: max-content; 
        border: 1px solid #e5e7eb;
        
        transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out, visibility 0.2s linear;
    }

    .custom-icon-wrapper:hover .custom-dropdown-content { 
        visibility: visible !important; 
        opacity: 1 !important; 
        pointer-events: auto !important; 
        transform: translateY(0);
        transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out, visibility 0s linear;
    }
    
    .custom-dropdown-item { 
        display: flex !important; 
        flex-direction: column !important; 
        align-items: flex-start !important; 
        padding: 0 !important; 
        color: #4b5563 !important; 
        text-decoration: none !important; 
        font-size: 14px !important; 
        font-weight: 500 !important; 
        line-height: 1 !important;
        position: relative !important; 
        cursor: pointer; 
    }

    .custom-dropdown-item > span.flex { width: 100%; display: flex; align-items: center; justify-content: flex-start; padding: 8px 10px !important; gap: 8px; line-height: 1 !important; }
    .custom-dropdown-item > span.flex i.fa-angle-down { margin-left: auto !important; }
    
    .custom-nested-menu { 
        display: none !important; 
        position: absolute !important; 
        left: 100% !important; 
        top: 0 !important;
        background-color: white !important;
        box-shadow: 0 30px 40px 0 rgba(0, 0, 0, 0.07) !important;
        border-radius: 15px !important; 
        padding: 8px 0 !important; 
        min-width: 200px; 
        z-index: 10000 !important; 
        border: 1px solid #e5e7eb;
    }

    .custom-dropdown-item:hover > .custom-nested-menu { display: block !important; }
    .custom-nested-menu a { display: flex !important; align-items: center; padding: 8px 16px !important; color: #4b5563 !important; text-decoration: none !important; font-size: 14px !important; font-weight: 500 !important; line-height: 1 !important; }
    
    #hl-active-icon-container { display: flex; align-items: center; justify-content: center; margin-right: 8px; }
    #hl-active-icon-container svg, #hl-active-icon-container img, #hl-active-icon-container i { width: 18px !important; height: 17px !important; display: block !important; }
`;
      document.head.appendChild(style);
    }

    let wrapper = navContainer.querySelector(".custom-nav-wrapper");
    if (!wrapper) {
      wrapper = document.createElement("div");
      wrapper.className = "custom-nav-wrapper";
      wrapper.innerHTML = `
        <button class="custom-menu-btn"><span class="custom-menu-text"></span></button>
        <div class="custom-icon-wrapper">
          <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.25153 12.0784C9.22552 11.4373 10.0199 10.6963 10.7015 9.78847C10.8876 9.5407 10.9806 9.41682 10.9806 9.25C10.9806 9.08318 10.8876 8.9593 10.7015 8.71154C10.0199 7.80367 9.22552 7.06266 8.25153 6.42157M9.75 17.75L8.75 17.75C4.97876 17.75 3.09315 17.75 1.92157 16.5784C0.75 15.4069 0.75 13.5212 0.75 9.75L0.75 8.75C0.75 4.97876 0.749999 3.09314 1.92157 1.92157C3.09315 0.749996 4.97876 0.749996 8.75 0.749997L9.75 0.749997C13.5212 0.749997 15.4069 0.749997 16.5784 1.92157C17.75 3.09314 17.75 4.97876 17.75 8.75L17.75 9.75C17.75 13.5212 17.75 15.4069 16.5784 16.5784C15.4069 17.75 13.5212 17.75 9.75 17.75Z" stroke="#CFCFD6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <div class="custom-dropdown-content"></div>
        </div>`;
      navContainer.appendChild(wrapper);
    }

    const data = getActiveSidebarData();

    const textSpan = wrapper.querySelector(".custom-menu-text");
    if (textSpan && data.text !== lastActiveText) {
      textSpan.innerText = data.text;
      lastActiveText = data.text;
    }

const activeIconContainer = document.getElementById("hl-active-icon-container");

if (
  activeIconContainer &&
  !window.location.pathname.includes("/tasks") &&
  !window.location.pathname.includes("/contacts") &&
  data.icon !== lastActiveIcon
) {
  activeIconContainer.innerHTML = data.icon;
  lastActiveIcon = data.icon;
}

    if (originalLinks.length > 0) {
      const currentMenuHtml = originalLinks.map(link => link.outerHTML).join("");

      if (currentMenuHtml !== lastMenuHtml) {
        const dropdownContent = wrapper.querySelector(".custom-dropdown-content");
        dropdownContent.innerHTML = "";

        originalLinks.forEach(link => {
          const clone = link.cloneNode(true);
          clone.className = "custom-dropdown-item";

          if (clone.id === "tb_conversations-tab" && window.location.href.includes("conversations/conversations")) {
            clone.setAttribute("aria-current", "page");
          }

          const nestedMenus = clone.querySelectorAll('[role="menu"], .dropdown-content');
          nestedMenus.forEach(menu => {
            if (menu.querySelectorAll("a").length === 0) {
              menu.remove();
            } else {
              menu.className = "custom-nested-menu";
            }
          });
          dropdownContent.appendChild(clone);
        });

        lastMenuHtml = currentMenuHtml;
      }

    }

    if (domObserver) domObserver.observe(document.body, { childList: true, subtree: true });
  };

  let debounceTimer;
  const requestUpdate = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(buildOrUpdateMenu, 150);
  };

  domObserver = new MutationObserver(() => requestUpdate());
  domObserver.observe(document.body, { childList: true, subtree: true });

  window.addEventListener("popstate", requestUpdate);
  window.addEventListener("locationchange", requestUpdate);
  window.addEventListener("ghl-theme:route-change", requestUpdate);
  const patchState = type => {
    const orig = history[type];
    history[type] = function () {
      const res = orig.apply(this, arguments);
      requestUpdate();
      return res;
    };
  };
  patchState("pushState");
  patchState("replaceState");

  requestUpdate();
};

initScript();

const titleTagMutationObserver = new MutationObserver(() => {
  const url = window.location.href;
  const locElement = document.querySelector("#location-switcher-sidbar-v2 .hl_switcher-loc-name");

  if (locElement && locElement.innerText.trim() !== "") {
    const locationName = locElement.innerText.trim();
    const suffix = ` | ${locationName}`;

    const pages = {
      "/dashboard": "Leads Dashboard",
      "/conversations/": "Conversations",
      "/my-website/": "My Website",
      "/marketing/": "Marketing",
      "/payments/": "Payments",
      "/agency-analytics-login": "Insights",
      "/opportunities/": "Opportunities",
      "/calendars/": "Calendars",
      "/contacts/": "Contacts",
      "/tasks": "Tasks",
      "/reputation/": "Reputation",
      "/automation/": "Automation",
      "/social-planner": "Social Planner",
      "/billing": "Billing",
      "/business-info/": "Business Info",
      "/business": "Companies",
      "/survey": "Sites",
      "/settings": "Settings",
      "/wordpress": "Sites",
      "/reporting": "Reporting",
      "/analytics": "Analytics",
      "/memberships": "Memberships",
      "/funnels-websites": "Sites",
      "/ai-agents": "AI Agents",
      "/agency_dashboard": "Agency Dashboard",
      "/form-builder/": "Sites"
    };

    for (const path in pages) {
      if (url.includes(path)) {
        const newTitle = pages[path] + suffix;
        if (document.title !== newTitle) {
          document.title = newTitle;
        }
        break;
      }
    }
  }
});

titleTagMutationObserver.observe(document.body, {
  childList: true,
  subtree: true
});

window.addEventListener("ghl-theme:route-change", () => {
  GHLThemeLifecycle.schedule("title-route-change", 0);
});

const notSettingsPage = [
  "affiliate-manager",
  "/memberships/courses/settings",
  "/automation/workflows/settings",
  "/settings/smart_list/Settings",
  "funnels-websites/client-portal",
  "/funnels-websites/client-portal/dashboard",
  "/conversation-ai/",
  "conversations",
  "/memberships/client-portal/",
  "/funnels-websites/funnels/",
  "/reputation/settings",
  "/marketing/brand-boards",
  "/receipts",
  "/payments",
  "/marketing/social-planner",
  "/automation/campaigns"
];

function checkIsSettingsPage() {
  if (isPaymentsIntegrationsDashboardRoute()) return true;

  if (!window.location.href.includes("settings")) return false;

  return !notSettingsPage.some(path => window.location.href.includes(path));
}

const syncSettingsPageClass = () => {
  if (checkIsSettingsPage()) {
    document.body.classList.add("is-settings-page-custom");
  } else {
    document.body.classList.remove("is-settings-page-custom");
  }

  if (isPaymentsIntegrationsDashboardRoute()) {
    document.body.classList.add("is-payments-integrations-dashboard");
  } else {
    document.body.classList.remove("is-payments-integrations-dashboard");
  }
};

registerSpaTask("settings-page-class", syncSettingsPageClass, {
  intervalMs: 1000,
  minIntervalMs: 100
});

const routeWatcher = new MutationObserver(() => {
  const isSettingsPage = window.location.href.includes("/settings/calendars");
  const hasClass = document.body.classList.contains("on-calendar-settings");

  if (isSettingsPage && !hasClass) {
    routeWatcher.disconnect();
    document.body.classList.add("on-calendar-settings");
    startObserving();
  } else if (!isSettingsPage && hasClass) {
    routeWatcher.disconnect();
    document.body.classList.remove("on-calendar-settings");
    startObserving();
  }
});

function startObserving() {
  routeWatcher.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
}

startObserving();

syncSettingsPageClass();

// var leadsInterval = setInterval(function() {
//   var el = document.querySelector('.hl-top-left');
//   if (el && el.innerHTML.includes("Opportunities")) {
//     el.innerHTML = el.innerHTML.replace(/Opportunities/g, "Leads");
//     clearInterval(leadsInterval);
//   }
// }, 500);

setTimeout(() => {
  const targetNav = document.querySelector(".hl_nav-header");
  const toggleContainer = document.querySelector("#sidebar-v2 .absolute.z-50.-right-2.bottom-5");

  if (targetNav && toggleContainer) {
    targetNav.style.position = "relative";
    targetNav.prepend(toggleContainer);

    toggleContainer.style.bottom = "auto";
    toggleContainer.style.top = "0";
    toggleContainer.style.right = "-12px";
  }
}, 1000);

(function () {
  const iconMap = {
    sb_dashboard: `<svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.32141 6.80505L2.89485 7.03053C1.46498 7.78635 0.750048 8.16426 0.750048 8.75028C0.750048 9.33629 1.46498 9.7142 2.89485 10.47L4.74415 11.4476C6.52496 12.3889 7.41537 12.8595 8.38035 12.8595C9.34532 12.8595 10.2357 12.3889 12.0166 11.4476L13.8658 10.47C15.2957 9.7142 16.0106 9.33629 16.0106 8.75028C16.0106 8.16426 15.2957 7.78635 13.8658 7.03053L13.4393 6.80505M3.32141 10.6955L2.89485 10.921C1.46498 11.6768 0.750048 12.0547 0.750048 12.6407C0.750048 13.2267 1.46498 13.6047 2.89485 14.3605L4.74415 15.338C6.52496 16.2793 7.41537 16.75 8.38035 16.75C9.34532 16.75 10.2357 16.2793 12.0166 15.338L13.8658 14.3605C15.2957 13.6047 16.0106 13.2267 16.0106 12.6407C16.0106 12.0547 15.2957 11.6768 13.8658 10.921L13.4393 10.6955M2.8948 6.57902L4.7441 7.55655C6.52491 8.49788 7.41532 8.96855 8.3803 8.96855C9.34528 8.96855 10.2357 8.49788 12.0165 7.55655L13.8658 6.57902C15.2957 5.8232 16.0106 5.44529 16.0106 4.85927C16.0106 4.27326 15.2957 3.89535 13.8658 3.13953L12.0165 2.162C10.2357 1.22067 9.34528 0.75 8.3803 0.75C7.41532 0.75 6.52491 1.22067 4.7441 2.162L2.8948 3.13953C1.46493 3.89535 0.75 4.27326 0.75 4.85927C0.75 5.44529 1.46493 5.8232 2.8948 6.57902Z" stroke="url(#paint0_linear_13016_5773)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M3.32141 6.80505L2.89485 7.03053C1.46498 7.78635 0.750048 8.16426 0.750048 8.75028C0.750048 9.33629 1.46498 9.7142 2.89485 10.47L4.74415 11.4476C6.52496 12.3889 7.41537 12.8595 8.38035 12.8595C9.34532 12.8595 10.2357 12.3889 12.0166 11.4476L13.8658 10.47C15.2957 9.7142 16.0106 9.33629 16.0106 8.75028C16.0106 8.16426 15.2957 7.78635 13.8658 7.03053L13.4393 6.80505M3.32141 10.6955L2.89485 10.921C1.46498 11.6768 0.750048 12.0547 0.750048 12.6407C0.750048 13.2267 1.46498 13.6047 2.89485 14.3605L4.74415 15.338C6.52496 16.2793 7.41537 16.75 8.38035 16.75C9.34532 16.75 10.2357 16.2793 12.0166 15.338L13.8658 14.3605C15.2957 13.6047 16.0106 13.2267 16.0106 12.6407C16.0106 12.0547 15.2957 11.6768 13.8658 10.921L13.4393 10.6955M2.8948 6.57902L4.7441 7.55655C6.52491 8.49788 7.41532 8.96855 8.3803 8.96855C9.34528 8.96855 10.2357 8.49788 12.0165 7.55655L13.8658 6.57902C15.2957 5.8232 16.0106 5.44529 16.0106 4.85927C16.0106 4.27326 15.2957 3.89535 13.8658 3.13953L12.0165 2.162C10.2357 1.22067 9.34528 0.75 8.3803 0.75C7.41532 0.75 6.52491 1.22067 4.7441 2.162L2.8948 3.13953C1.46493 3.89535 0.75 4.27326 0.75 4.85927C0.75 5.44529 1.46493 5.8232 2.8948 6.57902Z" stroke="url(#paint1_linear_13016_5773)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M3.32141 6.80505L2.89485 7.03053C1.46498 7.78635 0.750048 8.16426 0.750048 8.75028C0.750048 9.33629 1.46498 9.7142 2.89485 10.47L4.74415 11.4476C6.52496 12.3889 7.41537 12.8595 8.38035 12.8595C9.34532 12.8595 10.2357 12.3889 12.0166 11.4476L13.8658 10.47C15.2957 9.7142 16.0106 9.33629 16.0106 8.75028C16.0106 8.16426 15.2957 7.78635 13.8658 7.03053L13.4393 6.80505M3.32141 10.6955L2.89485 10.921C1.46498 11.6768 0.750048 12.0547 0.750048 12.6407C0.750048 13.2267 1.46498 13.6047 2.89485 14.3605L4.74415 15.338C6.52496 16.2793 7.41537 16.75 8.38035 16.75C9.34532 16.75 10.2357 16.2793 12.0166 15.338L13.8658 14.3605C15.2957 13.6047 16.0106 13.2267 16.0106 12.6407C16.0106 12.0547 15.2957 11.6768 13.8658 10.921L13.4393 10.6955M2.8948 6.57902L4.7441 7.55655C6.52491 8.49788 7.41532 8.96855 8.3803 8.96855C9.34528 8.96855 10.2357 8.49788 12.0165 7.55655L13.8658 6.57902C15.2957 5.8232 16.0106 5.44529 16.0106 4.85927C16.0106 4.27326 15.2957 3.89535 13.8658 3.13953L12.0165 2.162C10.2357 1.22067 9.34528 0.75 8.3803 0.75C7.41532 0.75 6.52491 1.22067 4.7441 2.162L2.8948 3.13953C1.46493 3.89535 0.75 4.27326 0.75 4.85927C0.75 5.44529 1.46493 5.8232 2.8948 6.57902Z" stroke="url(#paint2_linear_13016_5773)" stroke-width="1.5" stroke-linejoin="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5773" x1="16.7598" y1="0.954602" x2="-0.199508" y2="5.17641" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5773" x1="16.0106" y1="-7.95605" x2="-0.999313" y2="-6.94252" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5773" x1="16.0106" y1="-7.95605" x2="-0.999313" y2="-6.94252" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_conversations: `<svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4.96053 5.38158H12.1184M4.96053 8.75H7.90789M5.17105 13.3816H10.4342C13.0202 13.3816 14.3133 13.3816 15.2305 12.7409C15.5728 12.5018 15.8702 12.2043 16.1093 11.8621C16.75 10.9448 16.75 9.65182 16.75 7.06579C16.75 4.47975 16.75 3.18674 16.1093 2.26952C15.8702 1.92724 15.5728 1.62975 15.2305 1.39068C14.3133 0.75 13.0202 0.75 10.4342 0.75H7.86111C4.5089 0.75 2.8328 0.75 1.7914 1.7914C0.75 2.8328 0.75 4.5089 0.75 7.86111V12.75C0.75 13.9417 0.75 14.5375 1.05861 14.8035C1.17035 14.8998 1.3043 14.9667 1.44838 14.9983C1.84631 15.0857 2.32298 14.7282 3.27632 14.0132C3.6407 13.7399 3.8229 13.6032 4.02901 13.5176C4.10782 13.4849 4.18888 13.4578 4.27156 13.4367C4.48783 13.3816 4.71557 13.3816 5.17105 13.3816Z" stroke="url(#paint0_linear_13016_5772)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M4.96053 5.38158H12.1184M4.96053 8.75H7.90789M5.17105 13.3816H10.4342C13.0202 13.3816 14.3133 13.3816 15.2305 12.7409C15.5728 12.5018 15.8702 12.2043 16.1093 11.8621C16.75 10.9448 16.75 9.65182 16.75 7.06579C16.75 4.47975 16.75 3.18674 16.1093 2.26952C15.8702 1.92724 15.5728 1.62975 15.2305 1.39068C14.3133 0.75 13.0202 0.75 10.4342 0.75H7.86111C4.5089 0.75 2.8328 0.75 1.7914 1.7914C0.75 2.8328 0.75 4.5089 0.75 7.86111V12.75C0.75 13.9417 0.75 14.5375 1.05861 14.8035C1.17035 14.8998 1.3043 14.9667 1.44838 14.9983C1.84631 15.0857 2.32298 14.7282 3.27632 14.0132C3.6407 13.7399 3.8229 13.6032 4.02901 13.5176C4.10782 13.4849 4.18888 13.4578 4.27156 13.4367C4.48783 13.3816 4.71557 13.3816 5.17105 13.3816Z" stroke="url(#paint1_linear_13016_5772)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M4.96053 5.38158H12.1184M4.96053 8.75H7.90789M5.17105 13.3816H10.4342C13.0202 13.3816 14.3133 13.3816 15.2305 12.7409C15.5728 12.5018 15.8702 12.2043 16.1093 11.8621C16.75 10.9448 16.75 9.65182 16.75 7.06579C16.75 4.47975 16.75 3.18674 16.1093 2.26952C15.8702 1.92724 15.5728 1.62975 15.2305 1.39068C14.3133 0.75 13.0202 0.75 10.4342 0.75H7.86111C4.5089 0.75 2.8328 0.75 1.7914 1.7914C0.75 2.8328 0.75 4.5089 0.75 7.86111V12.75C0.75 13.9417 0.75 14.5375 1.05861 14.8035C1.17035 14.8998 1.3043 14.9667 1.44838 14.9983C1.84631 15.0857 2.32298 14.7282 3.27632 14.0132C3.6407 13.7399 3.8229 13.6032 4.02901 13.5176C4.10782 13.4849 4.18888 13.4578 4.27156 13.4367C4.48783 13.3816 4.71557 13.3816 5.17105 13.3816Z" stroke="url(#paint2_linear_13016_5772)" stroke-width="1.5" stroke-linecap="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5772" x1="17.5355" y1="0.932368" x2="0.143813" y2="6.02496" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5772" x1="16.75" y1="-7.00997" x2="-1.05989" y2="-5.76172" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5772" x1="16.75" y1="-7.00997" x2="-1.05989" y2="-5.76172" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_tasks: `<svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.49443 1.99344H16.235M7.49443 8.79167H16.235M7.49443 15.5899H16.235M0.75 1.78007C1.32537 2.14243 1.77024 2.57366 2.12347 3.15354C2.82336 2.23681 3.61318 1.45443 4.52701 0.75M0.75 8.5783C1.32537 8.94066 1.77024 9.37189 2.12347 9.95177C2.82336 9.03504 3.61318 8.25266 4.52701 7.54823M0.75 15.3765C1.32537 15.7389 1.77024 16.1701 2.12347 16.75C2.82336 15.8333 3.61318 15.0509 4.52701 14.3465" stroke="url(#paint0_linear_13016_5771)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M7.49443 1.99344H16.235M7.49443 8.79167H16.235M7.49443 15.5899H16.235M0.75 1.78007C1.32537 2.14243 1.77024 2.57366 2.12347 3.15354C2.82336 2.23681 3.61318 1.45443 4.52701 0.75M0.75 8.5783C1.32537 8.94066 1.77024 9.37189 2.12347 9.95177C2.82336 9.03504 3.61318 8.25266 4.52701 7.54823M0.75 15.3765C1.32537 15.7389 1.77024 16.1701 2.12347 16.75C2.82336 15.8333 3.61318 15.0509 4.52701 14.3465" stroke="url(#paint1_linear_13016_5771)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M7.49443 1.99344H16.235M7.49443 8.79167H16.235M7.49443 15.5899H16.235M0.75 1.78007C1.32537 2.14243 1.77024 2.57366 2.12347 3.15354C2.82336 2.23681 3.61318 1.45443 4.52701 0.75M0.75 8.5783C1.32537 8.94066 1.77024 9.37189 2.12347 9.95177C2.82336 9.03504 3.61318 8.25266 4.52701 7.54823M0.75 15.3765C1.32537 15.7389 1.77024 16.1701 2.12347 16.75C2.82336 15.8333 3.61318 15.0509 4.52701 14.3465" stroke="url(#paint2_linear_13016_5771)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5771" x1="16.9952" y1="0.954602" x2="-0.183774" y2="5.29396" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5771" x1="16.235" y1="-7.95605" x2="-1.02322" y2="-6.91261" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5771" x1="16.235" y1="-7.95605" x2="-1.02322" y2="-6.91261" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_calendars: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M13.2566 0.745149C13.2539 0.330944 12.916 -0.00266361 12.5018 1.5676e-05C12.0876 0.00269502 11.754 0.340646 11.7566 0.754851L12.5066 0.75L13.2566 0.745149ZM11.7661 2.2094C11.7687 2.6236 12.1067 2.95721 12.5209 2.95453C12.9351 2.95185 13.2687 2.6139 13.266 2.19969L12.516 2.20455L11.7661 2.2094ZM5.72956 0.745148C5.72688 0.330944 5.38893 -0.00266373 4.97472 1.5676e-05C4.56052 0.00269514 4.22691 0.340647 4.22959 0.754852L4.97958 0.75L5.72956 0.745148ZM4.239 2.2094C4.24168 2.6236 4.57963 2.95721 4.99384 2.95453C5.40804 2.95185 5.74165 2.6139 5.73897 2.19969L4.98899 2.20455L4.239 2.2094ZM4.97958 8.72727C4.56536 8.72727 4.22958 9.06306 4.22958 9.47727C4.22958 9.89149 4.56536 10.2273 4.97958 10.2273V9.47727V8.72727ZM4.98899 10.2273C5.4032 10.2273 5.73899 9.89149 5.73899 9.47727C5.73899 9.06306 5.4032 8.72727 4.98899 8.72727V9.47727V10.2273ZM4.97958 11.6364C4.56536 11.6364 4.22958 11.9722 4.22958 12.3864C4.22958 12.8006 4.56536 13.1364 4.97958 13.1364V12.3864V11.6364ZM4.98899 13.1364C5.4032 13.1364 5.73899 12.8006 5.73899 12.3864C5.73899 11.9722 5.4032 11.6364 4.98899 11.6364V12.3864V13.1364ZM8.74311 8.72727C8.32889 8.72727 7.99311 9.06306 7.99311 9.47727C7.99311 9.89149 8.32889 10.2273 8.74311 10.2273V9.47727V8.72727ZM8.75251 10.2273C9.16673 10.2273 9.50251 9.89149 9.50251 9.47727C9.50251 9.06306 9.16673 8.72727 8.75251 8.72727V9.47727V10.2273ZM8.74311 11.6364C8.32889 11.6364 7.99311 11.9722 7.99311 12.3864C7.99311 12.8006 8.32889 13.1364 8.74311 13.1364V12.3864V11.6364ZM8.75251 13.1364C9.16673 13.1364 9.50251 12.8006 9.50251 12.3864C9.50251 11.9722 9.16673 11.6364 8.75251 11.6364V12.3864V13.1364ZM12.5066 8.72727C12.0924 8.72727 11.7566 9.06306 11.7566 9.47727C11.7566 9.89149 12.0924 10.2273 12.5066 10.2273V9.47727V8.72727ZM12.516 10.2273C12.9303 10.2273 13.266 9.89149 13.266 9.47727C13.266 9.06306 12.9303 8.72727 12.516 8.72727V9.47727V10.2273ZM12.5066 11.6364C12.0924 11.6364 11.7566 11.9722 11.7566 12.3864C11.7566 12.8006 12.0924 13.1364 12.5066 13.1364V12.3864V11.6364ZM12.516 13.1364C12.9303 13.1364 13.266 12.8006 13.266 12.3864C13.266 11.9722 12.9303 11.6364 12.516 11.6364V12.3864V13.1364ZM0.750209 9.63415L0.000208676 9.63367L0.750209 9.63415ZM16.7452 9.64363L17.4952 9.64412V9.64412L16.7452 9.64363ZM16.7454 9.3204L15.9954 9.31991V9.31991L16.7454 9.3204ZM0.750418 9.31091L1.50042 9.3114L0.750418 9.31091ZM7.65022 2.20455V2.95455H9.84561V2.20455V1.45455H7.65022V2.20455ZM16.7454 9.3204L15.9954 9.31991L15.9952 9.64315L16.7452 9.64363L17.4952 9.64412L17.4954 9.32088L16.7454 9.3204ZM9.8454 16.75V16H7.65001V16.75V17.5H9.8454V16.75ZM0.750209 9.63415L1.50021 9.63463L1.50042 9.3114L0.750418 9.31091L0.000417769 9.31043L0.000208676 9.63367L0.750209 9.63415ZM0.745605 6.56818V7.31818H16.7406V6.56818V5.81818H0.745605V6.56818ZM12.5066 0.75L11.7566 0.754851L11.7661 2.2094L12.516 2.20455L13.266 2.19969L13.2566 0.745149L12.5066 0.75ZM4.97958 0.75L4.22959 0.754852L4.239 2.2094L4.98899 2.20455L5.73897 2.19969L5.72956 0.745148L4.97958 0.75ZM4.97958 9.47727V10.2273H4.98899V9.47727V8.72727H4.97958V9.47727ZM4.97958 12.3864V13.1364H4.98899V12.3864V11.6364H4.97958V12.3864ZM8.74311 9.47727V10.2273H8.75251V9.47727V8.72727H8.74311V9.47727ZM8.74311 12.3864V13.1364H8.75251V12.3864V11.6364H8.74311V12.3864ZM12.5066 9.47727V10.2273H12.516V9.47727V8.72727H12.5066V9.47727ZM12.5066 12.3864V13.1364H12.516V12.3864V11.6364H12.5066V12.3864ZM7.65001 16.75V16C6.00088 16 4.84178 15.9983 3.96525 15.8768C3.11267 15.7587 2.63968 15.5401 2.29599 15.1857L1.75755 15.7078L1.2191 16.2299C1.886 16.9176 2.73195 17.2202 3.75932 17.3626C4.76272 17.5017 6.04456 17.5 7.65001 17.5V16.75ZM0.750209 9.63415L0.000208676 9.63367C-0.000862896 11.2902 -0.00321597 12.6058 0.130333 13.6345C0.26639 14.6825 0.555276 15.5452 1.2191 16.2299L1.75755 15.7078L2.29599 15.1857C1.94922 14.828 1.73336 14.3311 1.61785 13.4414C1.49984 12.5324 1.49911 11.3324 1.50021 9.63463L0.750209 9.63415ZM16.7452 9.64363L15.9952 9.64315C15.9941 11.3388 15.9918 12.5373 15.8728 13.4452C15.7564 14.3337 15.5402 14.8299 15.1935 15.187L15.7316 15.7094L16.2697 16.2319C16.9334 15.5484 17.2229 14.6868 17.3601 13.6401C17.4948 12.6127 17.4941 11.2986 17.4952 9.64412L16.7452 9.64363ZM9.8454 16.75V17.5C11.4489 17.5 12.7292 17.5017 13.7315 17.3628C14.7578 17.2207 15.603 16.9186 16.2697 16.2319L15.7316 15.7094L15.1935 15.187C14.8499 15.5409 14.3773 15.7591 13.5257 15.877C12.6502 15.9983 11.4925 16 9.8454 16V16.75ZM9.84561 2.20455V2.95455C11.4947 2.95455 12.6538 2.95624 13.5304 3.07772C14.3829 3.19589 14.8559 3.41442 15.1996 3.76887L15.7381 3.24678L16.2765 2.72469C15.6096 2.03691 14.7637 1.73432 13.7363 1.59193C12.7329 1.45285 11.4511 1.45455 9.84561 1.45455V2.20455ZM16.7454 9.3204L17.4954 9.32088C17.4965 7.66435 17.4988 6.3487 17.3653 5.32002C17.2292 4.27204 16.9403 3.4093 16.2765 2.72469L15.7381 3.24678L15.1996 3.76887C15.5464 4.1265 15.7623 4.62345 15.8778 5.51314C15.9958 6.42215 15.9965 7.62219 15.9954 9.31991L16.7454 9.3204ZM7.65022 2.20455V1.45455C6.04674 1.45455 4.76642 1.45286 3.76407 1.59171C2.73777 1.73388 1.89259 2.03599 1.22589 2.72265L1.76398 3.24511L2.30207 3.76756C2.64568 3.41366 3.11831 3.19549 3.9699 3.07752C4.84544 2.95623 6.00309 2.95455 7.65022 2.95455V2.20455ZM0.750418 9.31091L1.50042 9.3114C1.50151 7.61573 1.50379 6.4172 1.62278 5.50938C1.73925 4.62087 1.9554 4.12461 2.30207 3.76756L1.76398 3.24511L1.22589 2.72265C0.562251 3.40616 0.272705 4.26772 0.135506 5.31443C0.000837684 6.34184 0.00148803 7.65593 0.000417769 9.31043L0.750418 9.31091Z" fill="url(#paint0_linear_13016_5770)"/>
<path d="M13.2566 0.745149C13.2539 0.330944 12.916 -0.00266361 12.5018 1.5676e-05C12.0876 0.00269502 11.754 0.340646 11.7566 0.754851L12.5066 0.75L13.2566 0.745149ZM11.7661 2.2094C11.7687 2.6236 12.1067 2.95721 12.5209 2.95453C12.9351 2.95185 13.2687 2.6139 13.266 2.19969L12.516 2.20455L11.7661 2.2094ZM5.72956 0.745148C5.72688 0.330944 5.38893 -0.00266373 4.97472 1.5676e-05C4.56052 0.00269514 4.22691 0.340647 4.22959 0.754852L4.97958 0.75L5.72956 0.745148ZM4.239 2.2094C4.24168 2.6236 4.57963 2.95721 4.99384 2.95453C5.40804 2.95185 5.74165 2.6139 5.73897 2.19969L4.98899 2.20455L4.239 2.2094ZM4.97958 8.72727C4.56536 8.72727 4.22958 9.06306 4.22958 9.47727C4.22958 9.89149 4.56536 10.2273 4.97958 10.2273V9.47727V8.72727ZM4.98899 10.2273C5.4032 10.2273 5.73899 9.89149 5.73899 9.47727C5.73899 9.06306 5.4032 8.72727 4.98899 8.72727V9.47727V10.2273ZM4.97958 11.6364C4.56536 11.6364 4.22958 11.9722 4.22958 12.3864C4.22958 12.8006 4.56536 13.1364 4.97958 13.1364V12.3864V11.6364ZM4.98899 13.1364C5.4032 13.1364 5.73899 12.8006 5.73899 12.3864C5.73899 11.9722 5.4032 11.6364 4.98899 11.6364V12.3864V13.1364ZM8.74311 8.72727C8.32889 8.72727 7.99311 9.06306 7.99311 9.47727C7.99311 9.89149 8.32889 10.2273 8.74311 10.2273V9.47727V8.72727ZM8.75251 10.2273C9.16673 10.2273 9.50251 9.89149 9.50251 9.47727C9.50251 9.06306 9.16673 8.72727 8.75251 8.72727V9.47727V10.2273ZM8.74311 11.6364C8.32889 11.6364 7.99311 11.9722 7.99311 12.3864C7.99311 12.8006 8.32889 13.1364 8.74311 13.1364V12.3864V11.6364ZM8.75251 13.1364C9.16673 13.1364 9.50251 12.8006 9.50251 12.3864C9.50251 11.9722 9.16673 11.6364 8.75251 11.6364V12.3864V13.1364ZM12.5066 8.72727C12.0924 8.72727 11.7566 9.06306 11.7566 9.47727C11.7566 9.89149 12.0924 10.2273 12.5066 10.2273V9.47727V8.72727ZM12.516 10.2273C12.9303 10.2273 13.266 9.89149 13.266 9.47727C13.266 9.06306 12.9303 8.72727 12.516 8.72727V9.47727V10.2273ZM12.5066 11.6364C12.0924 11.6364 11.7566 11.9722 11.7566 12.3864C11.7566 12.8006 12.0924 13.1364 12.5066 13.1364V12.3864V11.6364ZM12.516 13.1364C12.9303 13.1364 13.266 12.8006 13.266 12.3864C13.266 11.9722 12.9303 11.6364 12.516 11.6364V12.3864V13.1364ZM0.750209 9.63415L0.000208676 9.63367L0.750209 9.63415ZM16.7452 9.64363L17.4952 9.64412V9.64412L16.7452 9.64363ZM16.7454 9.3204L15.9954 9.31991V9.31991L16.7454 9.3204ZM0.750418 9.31091L1.50042 9.3114L0.750418 9.31091ZM7.65022 2.20455V2.95455H9.84561V2.20455V1.45455H7.65022V2.20455ZM16.7454 9.3204L15.9954 9.31991L15.9952 9.64315L16.7452 9.64363L17.4952 9.64412L17.4954 9.32088L16.7454 9.3204ZM9.8454 16.75V16H7.65001V16.75V17.5H9.8454V16.75ZM0.750209 9.63415L1.50021 9.63463L1.50042 9.3114L0.750418 9.31091L0.000417769 9.31043L0.000208676 9.63367L0.750209 9.63415ZM0.745605 6.56818V7.31818H16.7406V6.56818V5.81818H0.745605V6.56818ZM12.5066 0.75L11.7566 0.754851L11.7661 2.2094L12.516 2.20455L13.266 2.19969L13.2566 0.745149L12.5066 0.75ZM4.97958 0.75L4.22959 0.754852L4.239 2.2094L4.98899 2.20455L5.73897 2.19969L5.72956 0.745148L4.97958 0.75ZM4.97958 9.47727V10.2273H4.98899V9.47727V8.72727H4.97958V9.47727ZM4.97958 12.3864V13.1364H4.98899V12.3864V11.6364H4.97958V12.3864ZM8.74311 9.47727V10.2273H8.75251V9.47727V8.72727H8.74311V9.47727ZM8.74311 12.3864V13.1364H8.75251V12.3864V11.6364H8.74311V12.3864ZM12.5066 9.47727V10.2273H12.516V9.47727V8.72727H12.5066V9.47727ZM12.5066 12.3864V13.1364H12.516V12.3864V11.6364H12.5066V12.3864ZM7.65001 16.75V16C6.00088 16 4.84178 15.9983 3.96525 15.8768C3.11267 15.7587 2.63968 15.5401 2.29599 15.1857L1.75755 15.7078L1.2191 16.2299C1.886 16.9176 2.73195 17.2202 3.75932 17.3626C4.76272 17.5017 6.04456 17.5 7.65001 17.5V16.75ZM0.750209 9.63415L0.000208676 9.63367C-0.000862896 11.2902 -0.00321597 12.6058 0.130333 13.6345C0.26639 14.6825 0.555276 15.5452 1.2191 16.2299L1.75755 15.7078L2.29599 15.1857C1.94922 14.828 1.73336 14.3311 1.61785 13.4414C1.49984 12.5324 1.49911 11.3324 1.50021 9.63463L0.750209 9.63415ZM16.7452 9.64363L15.9952 9.64315C15.9941 11.3388 15.9918 12.5373 15.8728 13.4452C15.7564 14.3337 15.5402 14.8299 15.1935 15.187L15.7316 15.7094L16.2697 16.2319C16.9334 15.5484 17.2229 14.6868 17.3601 13.6401C17.4948 12.6127 17.4941 11.2986 17.4952 9.64412L16.7452 9.64363ZM9.8454 16.75V17.5C11.4489 17.5 12.7292 17.5017 13.7315 17.3628C14.7578 17.2207 15.603 16.9186 16.2697 16.2319L15.7316 15.7094L15.1935 15.187C14.8499 15.5409 14.3773 15.7591 13.5257 15.877C12.6502 15.9983 11.4925 16 9.8454 16V16.75ZM9.84561 2.20455V2.95455C11.4947 2.95455 12.6538 2.95624 13.5304 3.07772C14.3829 3.19589 14.8559 3.41442 15.1996 3.76887L15.7381 3.24678L16.2765 2.72469C15.6096 2.03691 14.7637 1.73432 13.7363 1.59193C12.7329 1.45285 11.4511 1.45455 9.84561 1.45455V2.20455ZM16.7454 9.3204L17.4954 9.32088C17.4965 7.66435 17.4988 6.3487 17.3653 5.32002C17.2292 4.27204 16.9403 3.4093 16.2765 2.72469L15.7381 3.24678L15.1996 3.76887C15.5464 4.1265 15.7623 4.62345 15.8778 5.51314C15.9958 6.42215 15.9965 7.62219 15.9954 9.31991L16.7454 9.3204ZM7.65022 2.20455V1.45455C6.04674 1.45455 4.76642 1.45286 3.76407 1.59171C2.73777 1.73388 1.89259 2.03599 1.22589 2.72265L1.76398 3.24511L2.30207 3.76756C2.64568 3.41366 3.11831 3.19549 3.9699 3.07752C4.84544 2.95623 6.00309 2.95455 7.65022 2.95455V2.20455ZM0.750418 9.31091L1.50042 9.3114C1.50151 7.61573 1.50379 6.4172 1.62278 5.50938C1.73925 4.62087 1.9554 4.12461 2.30207 3.76756L1.76398 3.24511L1.22589 2.72265C0.562251 3.40616 0.272705 4.26772 0.135506 5.31443C0.000837684 6.34184 0.00148803 7.65593 0.000417769 9.31043L0.750418 9.31091Z" fill="url(#paint1_linear_13016_5770)"/>
<path d="M13.2566 0.745149C13.2539 0.330944 12.916 -0.00266361 12.5018 1.5676e-05C12.0876 0.00269502 11.754 0.340646 11.7566 0.754851L12.5066 0.75L13.2566 0.745149ZM11.7661 2.2094C11.7687 2.6236 12.1067 2.95721 12.5209 2.95453C12.9351 2.95185 13.2687 2.6139 13.266 2.19969L12.516 2.20455L11.7661 2.2094ZM5.72956 0.745148C5.72688 0.330944 5.38893 -0.00266373 4.97472 1.5676e-05C4.56052 0.00269514 4.22691 0.340647 4.22959 0.754852L4.97958 0.75L5.72956 0.745148ZM4.239 2.2094C4.24168 2.6236 4.57963 2.95721 4.99384 2.95453C5.40804 2.95185 5.74165 2.6139 5.73897 2.19969L4.98899 2.20455L4.239 2.2094ZM4.97958 8.72727C4.56536 8.72727 4.22958 9.06306 4.22958 9.47727C4.22958 9.89149 4.56536 10.2273 4.97958 10.2273V9.47727V8.72727ZM4.98899 10.2273C5.4032 10.2273 5.73899 9.89149 5.73899 9.47727C5.73899 9.06306 5.4032 8.72727 4.98899 8.72727V9.47727V10.2273ZM4.97958 11.6364C4.56536 11.6364 4.22958 11.9722 4.22958 12.3864C4.22958 12.8006 4.56536 13.1364 4.97958 13.1364V12.3864V11.6364ZM4.98899 13.1364C5.4032 13.1364 5.73899 12.8006 5.73899 12.3864C5.73899 11.9722 5.4032 11.6364 4.98899 11.6364V12.3864V13.1364ZM8.74311 8.72727C8.32889 8.72727 7.99311 9.06306 7.99311 9.47727C7.99311 9.89149 8.32889 10.2273 8.74311 10.2273V9.47727V8.72727ZM8.75251 10.2273C9.16673 10.2273 9.50251 9.89149 9.50251 9.47727C9.50251 9.06306 9.16673 8.72727 8.75251 8.72727V9.47727V10.2273ZM8.74311 11.6364C8.32889 11.6364 7.99311 11.9722 7.99311 12.3864C7.99311 12.8006 8.32889 13.1364 8.74311 13.1364V12.3864V11.6364ZM8.75251 13.1364C9.16673 13.1364 9.50251 12.8006 9.50251 12.3864C9.50251 11.9722 9.16673 11.6364 8.75251 11.6364V12.3864V13.1364ZM12.5066 8.72727C12.0924 8.72727 11.7566 9.06306 11.7566 9.47727C11.7566 9.89149 12.0924 10.2273 12.5066 10.2273V9.47727V8.72727ZM12.516 10.2273C12.9303 10.2273 13.266 9.89149 13.266 9.47727C13.266 9.06306 12.9303 8.72727 12.516 8.72727V9.47727V10.2273ZM12.5066 11.6364C12.0924 11.6364 11.7566 11.9722 11.7566 12.3864C11.7566 12.8006 12.0924 13.1364 12.5066 13.1364V12.3864V11.6364ZM12.516 13.1364C12.9303 13.1364 13.266 12.8006 13.266 12.3864C13.266 11.9722 12.9303 11.6364 12.516 11.6364V12.3864V13.1364ZM0.750209 9.63415L0.000208676 9.63367L0.750209 9.63415ZM16.7452 9.64363L17.4952 9.64412V9.64412L16.7452 9.64363ZM16.7454 9.3204L15.9954 9.31991V9.31991L16.7454 9.3204ZM0.750418 9.31091L1.50042 9.3114L0.750418 9.31091ZM7.65022 2.20455V2.95455H9.84561V2.20455V1.45455H7.65022V2.20455ZM16.7454 9.3204L15.9954 9.31991L15.9952 9.64315L16.7452 9.64363L17.4952 9.64412L17.4954 9.32088L16.7454 9.3204ZM9.8454 16.75V16H7.65001V16.75V17.5H9.8454V16.75ZM0.750209 9.63415L1.50021 9.63463L1.50042 9.3114L0.750418 9.31091L0.000417769 9.31043L0.000208676 9.63367L0.750209 9.63415ZM0.745605 6.56818V7.31818H16.7406V6.56818V5.81818H0.745605V6.56818ZM12.5066 0.75L11.7566 0.754851L11.7661 2.2094L12.516 2.20455L13.266 2.19969L13.2566 0.745149L12.5066 0.75ZM4.97958 0.75L4.22959 0.754852L4.239 2.2094L4.98899 2.20455L5.73897 2.19969L5.72956 0.745148L4.97958 0.75ZM4.97958 9.47727V10.2273H4.98899V9.47727V8.72727H4.97958V9.47727ZM4.97958 12.3864V13.1364H4.98899V12.3864V11.6364H4.97958V12.3864ZM8.74311 9.47727V10.2273H8.75251V9.47727V8.72727H8.74311V9.47727ZM8.74311 12.3864V13.1364H8.75251V12.3864V11.6364H8.74311V12.3864ZM12.5066 9.47727V10.2273H12.516V9.47727V8.72727H12.5066V9.47727ZM12.5066 12.3864V13.1364H12.516V12.3864V11.6364H12.5066V12.3864ZM7.65001 16.75V16C6.00088 16 4.84178 15.9983 3.96525 15.8768C3.11267 15.7587 2.63968 15.5401 2.29599 15.1857L1.75755 15.7078L1.2191 16.2299C1.886 16.9176 2.73195 17.2202 3.75932 17.3626C4.76272 17.5017 6.04456 17.5 7.65001 17.5V16.75ZM0.750209 9.63415L0.000208676 9.63367C-0.000862896 11.2902 -0.00321597 12.6058 0.130333 13.6345C0.26639 14.6825 0.555276 15.5452 1.2191 16.2299L1.75755 15.7078L2.29599 15.1857C1.94922 14.828 1.73336 14.3311 1.61785 13.4414C1.49984 12.5324 1.49911 11.3324 1.50021 9.63463L0.750209 9.63415ZM16.7452 9.64363L15.9952 9.64315C15.9941 11.3388 15.9918 12.5373 15.8728 13.4452C15.7564 14.3337 15.5402 14.8299 15.1935 15.187L15.7316 15.7094L16.2697 16.2319C16.9334 15.5484 17.2229 14.6868 17.3601 13.6401C17.4948 12.6127 17.4941 11.2986 17.4952 9.64412L16.7452 9.64363ZM9.8454 16.75V17.5C11.4489 17.5 12.7292 17.5017 13.7315 17.3628C14.7578 17.2207 15.603 16.9186 16.2697 16.2319L15.7316 15.7094L15.1935 15.187C14.8499 15.5409 14.3773 15.7591 13.5257 15.877C12.6502 15.9983 11.4925 16 9.8454 16V16.75ZM9.84561 2.20455V2.95455C11.4947 2.95455 12.6538 2.95624 13.5304 3.07772C14.3829 3.19589 14.8559 3.41442 15.1996 3.76887L15.7381 3.24678L16.2765 2.72469C15.6096 2.03691 14.7637 1.73432 13.7363 1.59193C12.7329 1.45285 11.4511 1.45455 9.84561 1.45455V2.20455ZM16.7454 9.3204L17.4954 9.32088C17.4965 7.66435 17.4988 6.3487 17.3653 5.32002C17.2292 4.27204 16.9403 3.4093 16.2765 2.72469L15.7381 3.24678L15.1996 3.76887C15.5464 4.1265 15.7623 4.62345 15.8778 5.51314C15.9958 6.42215 15.9965 7.62219 15.9954 9.31991L16.7454 9.3204ZM7.65022 2.20455V1.45455C6.04674 1.45455 4.76642 1.45286 3.76407 1.59171C2.73777 1.73388 1.89259 2.03599 1.22589 2.72265L1.76398 3.24511L2.30207 3.76756C2.64568 3.41366 3.11831 3.19549 3.9699 3.07752C4.84544 2.95623 6.00309 2.95455 7.65022 2.95455V2.20455ZM0.750418 9.31091L1.50042 9.3114C1.50151 7.61573 1.50379 6.4172 1.62278 5.50938C1.73925 4.62087 1.9554 4.12461 2.30207 3.76756L1.76398 3.24511L1.22589 2.72265C0.562251 3.40616 0.272705 4.26772 0.135506 5.31443C0.000837684 6.34184 0.00148803 7.65593 0.000417769 9.31043L0.750418 9.31091Z" fill="url(#paint2_linear_13016_5770)"/>
<defs>
<linearGradient id="paint0_linear_13016_5770" x1="17.5311" y1="0.954602" x2="-0.147523" y2="5.56868" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5770" x1="16.7456" y1="-7.95605" x2="-1.0822" y2="-6.84233" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5770" x1="16.7456" y1="-7.95605" x2="-1.0822" y2="-6.84233" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_contacts: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M15.8611 10.5278H16.75M14.9722 7.86111H16.75M14.0833 5.19444H16.75M11.4167 4.30556C11.4167 6.26923 9.82479 7.86111 7.86111 7.86111C5.89743 7.86111 4.30556 6.26923 4.30556 4.30556C4.30556 2.34188 5.89743 0.75 7.86111 0.75C9.82479 0.75 11.4167 2.34188 11.4167 4.30556ZM14.9722 14.5278C14.9722 16.246 11.7885 16.75 7.86111 16.75C3.93375 16.75 0.75 16.246 0.75 14.5278C0.75 12.8096 3.93375 10.5278 7.86111 10.5278C11.7885 10.5278 14.9722 12.8096 14.9722 14.5278Z" stroke="url(#paint0_linear_13016_5769)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M15.8611 10.5278H16.75M14.9722 7.86111H16.75M14.0833 5.19444H16.75M11.4167 4.30556C11.4167 6.26923 9.82479 7.86111 7.86111 7.86111C5.89743 7.86111 4.30556 6.26923 4.30556 4.30556C4.30556 2.34188 5.89743 0.75 7.86111 0.75C9.82479 0.75 11.4167 2.34188 11.4167 4.30556ZM14.9722 14.5278C14.9722 16.246 11.7885 16.75 7.86111 16.75C3.93375 16.75 0.75 16.246 0.75 14.5278C0.75 12.8096 3.93375 10.5278 7.86111 10.5278C11.7885 10.5278 14.9722 12.8096 14.9722 14.5278Z" stroke="url(#paint1_linear_13016_5769)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M15.8611 10.5278H16.75M14.9722 7.86111H16.75M14.0833 5.19444H16.75M11.4167 4.30556C11.4167 6.26923 9.82479 7.86111 7.86111 7.86111C5.89743 7.86111 4.30556 6.26923 4.30556 4.30556C4.30556 2.34188 5.89743 0.75 7.86111 0.75C9.82479 0.75 11.4167 2.34188 11.4167 4.30556ZM14.9722 14.5278C14.9722 16.246 11.7885 16.75 7.86111 16.75C3.93375 16.75 0.75 16.246 0.75 14.5278C0.75 12.8096 3.93375 10.5278 7.86111 10.5278C11.7885 10.5278 14.9722 12.8096 14.9722 14.5278Z" stroke="url(#paint2_linear_13016_5769)" stroke-width="1.5" stroke-linecap="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5769" x1="17.5355" y1="0.954602" x2="-0.143129" y2="5.56868" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5769" x1="16.75" y1="-7.95605" x2="-1.07781" y2="-6.84233" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5769" x1="16.75" y1="-7.95605" x2="-1.07781" y2="-6.84233" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_opportunities: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M15.0949 0.75H2.40512C1.68567 0.75 1.32594 0.75 1.08202 0.953016C1.03192 0.99472 0.986326 1.04193 0.946053 1.09381C0.75 1.3464 0.75 1.7189 0.75 2.46391C0.75 3.02542 0.75 3.30618 0.834088 3.57914C0.850955 3.6339 0.873679 3.69675 0.895683 3.74951C1.00538 4.01255 1.17038 4.20876 1.5004 4.6012C2.65809 5.97787 3.91103 7.22577 5.29212 8.37697C6.03164 8.99338 6.40139 9.30159 6.5757 9.67858C6.75 10.0556 6.75 10.5091 6.75 11.4161V14.2008C6.75 14.7685 6.75 15.0524 6.88837 15.2781C7.02674 15.5039 7.27476 15.6248 7.7708 15.8665L8.21525 16.0831C9.35494 16.6385 9.92479 16.9162 10.3374 16.645C10.75 16.3739 10.75 15.7217 10.75 14.4174V11.4161C10.75 10.5091 10.75 10.0556 10.9243 9.67858C11.0986 9.30159 11.4684 8.99338 12.2079 8.37697C13.589 7.22577 14.8419 5.97787 15.9996 4.6012C16.3296 4.20876 16.4946 4.01255 16.6043 3.74951C16.6263 3.69675 16.649 3.6339 16.6659 3.57914C16.75 3.30618 16.75 3.02542 16.75 2.46391C16.75 1.7189 16.75 1.3464 16.5539 1.09381C16.5137 1.04193 16.4681 0.99472 16.418 0.953016C16.1741 0.75 15.8143 0.75 15.0949 0.75Z" stroke="url(#paint0_linear_13016_5768)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M15.0949 0.75H2.40512C1.68567 0.75 1.32594 0.75 1.08202 0.953016C1.03192 0.99472 0.986326 1.04193 0.946053 1.09381C0.75 1.3464 0.75 1.7189 0.75 2.46391C0.75 3.02542 0.75 3.30618 0.834088 3.57914C0.850955 3.6339 0.873679 3.69675 0.895683 3.74951C1.00538 4.01255 1.17038 4.20876 1.5004 4.6012C2.65809 5.97787 3.91103 7.22577 5.29212 8.37697C6.03164 8.99338 6.40139 9.30159 6.5757 9.67858C6.75 10.0556 6.75 10.5091 6.75 11.4161V14.2008C6.75 14.7685 6.75 15.0524 6.88837 15.2781C7.02674 15.5039 7.27476 15.6248 7.7708 15.8665L8.21525 16.0831C9.35494 16.6385 9.92479 16.9162 10.3374 16.645C10.75 16.3739 10.75 15.7217 10.75 14.4174V11.4161C10.75 10.5091 10.75 10.0556 10.9243 9.67858C11.0986 9.30159 11.4684 8.99338 12.2079 8.37697C13.589 7.22577 14.8419 5.97787 15.9996 4.6012C16.3296 4.20876 16.4946 4.01255 16.6043 3.74951C16.6263 3.69675 16.649 3.6339 16.6659 3.57914C16.75 3.30618 16.75 3.02542 16.75 2.46391C16.75 1.7189 16.75 1.3464 16.5539 1.09381C16.5137 1.04193 16.4681 0.99472 16.418 0.953016C16.1741 0.75 15.8143 0.75 15.0949 0.75Z" stroke="url(#paint1_linear_13016_5768)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M15.0949 0.75H2.40512C1.68567 0.75 1.32594 0.75 1.08202 0.953016C1.03192 0.99472 0.986326 1.04193 0.946053 1.09381C0.75 1.3464 0.75 1.7189 0.75 2.46391C0.75 3.02542 0.75 3.30618 0.834088 3.57914C0.850955 3.6339 0.873679 3.69675 0.895683 3.74951C1.00538 4.01255 1.17038 4.20876 1.5004 4.6012C2.65809 5.97787 3.91103 7.22577 5.29212 8.37697C6.03164 8.99338 6.40139 9.30159 6.5757 9.67858C6.75 10.0556 6.75 10.5091 6.75 11.4161V14.2008C6.75 14.7685 6.75 15.0524 6.88837 15.2781C7.02674 15.5039 7.27476 15.6248 7.7708 15.8665L8.21525 16.0831C9.35494 16.6385 9.92479 16.9162 10.3374 16.645C10.75 16.3739 10.75 15.7217 10.75 14.4174V11.4161C10.75 10.5091 10.75 10.0556 10.9243 9.67858C11.0986 9.30159 11.4684 8.99338 12.2079 8.37697C13.589 7.22577 14.8419 5.97787 15.9996 4.6012C16.3296 4.20876 16.4946 4.01255 16.6043 3.74951C16.6263 3.69675 16.649 3.6339 16.6659 3.57914C16.75 3.30618 16.75 3.02542 16.75 2.46391C16.75 1.7189 16.75 1.3464 16.5539 1.09381C16.5137 1.04193 16.4681 0.99472 16.418 0.953016C16.1741 0.75 15.8143 0.75 15.0949 0.75Z" stroke="url(#paint2_linear_13016_5768)" stroke-width="1.5" stroke-linejoin="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5768" x1="17.5355" y1="0.954602" x2="-0.143129" y2="5.56868" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5768" x1="16.75" y1="-7.95605" x2="-1.07781" y2="-6.84233" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5768" x1="16.75" y1="-7.95605" x2="-1.07781" y2="-6.84233" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_payments: `<svg width="13" height="18" viewBox="0 0 13 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M6.25 0.75V2.25M6.25 15.25V16.75M11.25 2.75C8.46653 2.30805 5.76611 2.2204 3.01682 2.47013C1.70751 2.58907 0.75 3.71967 0.75 5.03436V6.08333C0.75 7.55609 1.94391 8.75 3.41667 8.75H9.08333C10.5561 8.75 11.75 9.94391 11.75 11.4167V12.4656C11.75 13.7803 10.7925 14.9109 9.48318 15.0299C6.73389 15.2796 4.03347 15.192 1.25 14.75" stroke="url(#paint0_linear_13016_5774)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M6.25 0.75V2.25M6.25 15.25V16.75M11.25 2.75C8.46653 2.30805 5.76611 2.2204 3.01682 2.47013C1.70751 2.58907 0.75 3.71967 0.75 5.03436V6.08333C0.75 7.55609 1.94391 8.75 3.41667 8.75H9.08333C10.5561 8.75 11.75 9.94391 11.75 11.4167V12.4656C11.75 13.7803 10.7925 14.9109 9.48318 15.0299C6.73389 15.2796 4.03347 15.192 1.25 14.75" stroke="url(#paint1_linear_13016_5774)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M6.25 0.75V2.25M6.25 15.25V16.75M11.25 2.75C8.46653 2.30805 5.76611 2.2204 3.01682 2.47013C1.70751 2.58907 0.75 3.71967 0.75 5.03436V6.08333C0.75 7.55609 1.94391 8.75 3.41667 8.75H9.08333C10.5561 8.75 11.75 9.94391 11.75 11.4167V12.4656C11.75 13.7803 10.7925 14.9109 9.48318 15.0299C6.73389 15.2796 4.03347 15.192 1.25 14.75" stroke="url(#paint2_linear_13016_5774)" stroke-width="1.5" stroke-linecap="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5774" x1="12.29" y1="0.954602" x2="-0.287012" y2="3.21137" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5774" x1="11.75" y1="-7.95605" x2="-0.531794" y2="-7.42856" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5774" x1="11.75" y1="-7.95605" x2="-0.531794" y2="-7.42856" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    "sb_AI Agents": `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M18 0V18H0V0H18Z" fill="white" fill-opacity="0.01"/>
<path d="M6.81062 2.36339L6.91084 2.06271C7.19235 1.21819 8.38691 1.21819 8.66842 2.06272L8.76864 2.36339C9.66283 5.04596 11.6134 7.24723 14.1689 8.45771C14.828 8.76995 14.828 9.70791 14.1689 10.0201C11.6134 11.2306 9.66283 13.4319 8.76864 16.1145L8.66841 16.4151C8.38691 17.2597 7.19235 17.2597 6.91084 16.4151L6.81062 16.1145C5.91643 13.4319 3.96587 11.2306 1.41039 10.0201C0.751223 9.70791 0.751223 8.76995 1.41039 8.45771C3.96587 7.24723 5.91643 5.04596 6.81062 2.36339Z" stroke="url(#paint0_linear_13000_10040)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M6.81062 2.36339L6.91084 2.06271C7.19235 1.21819 8.38691 1.21819 8.66842 2.06272L8.76864 2.36339C9.66283 5.04596 11.6134 7.24723 14.1689 8.45771C14.828 8.76995 14.828 9.70791 14.1689 10.0201C11.6134 11.2306 9.66283 13.4319 8.76864 16.1145L8.66841 16.4151C8.38691 17.2597 7.19235 17.2597 6.91084 16.4151L6.81062 16.1145C5.91643 13.4319 3.96587 11.2306 1.41039 10.0201C0.751223 9.70791 0.751223 8.76995 1.41039 8.45771C3.96587 7.24723 5.91643 5.04596 6.81062 2.36339Z" stroke="url(#paint1_linear_13000_10040)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M6.81062 2.36339L6.91084 2.06271C7.19235 1.21819 8.38691 1.21819 8.66842 2.06272L8.76864 2.36339C9.66283 5.04596 11.6134 7.24723 14.1689 8.45771C14.828 8.76995 14.828 9.70791 14.1689 10.0201C11.6134 11.2306 9.66283 13.4319 8.76864 16.1145L8.66841 16.4151C8.38691 17.2597 7.19235 17.2597 6.91084 16.4151L6.81062 16.1145C5.91643 13.4319 3.96587 11.2306 1.41039 10.0201C0.751223 9.70791 0.751223 8.76995 1.41039 8.45771C3.96587 7.24723 5.91643 5.04596 6.81062 2.36339Z" stroke="url(#paint2_linear_13000_10040)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M14.9258 1.93274C15.204 2.34984 15.5602 2.71007 15.9746 2.99426C15.5603 3.27834 15.204 3.63883 14.9258 4.05579C14.6474 3.63879 14.2915 3.2783 13.877 2.99426C14.2915 2.71011 14.6474 2.34988 14.9258 1.93274Z" stroke="url(#paint3_linear_13000_10040)" stroke-width="2"/>
<path d="M14.9258 1.93274C15.204 2.34984 15.5602 2.71007 15.9746 2.99426C15.5603 3.27834 15.204 3.63883 14.9258 4.05579C14.6474 3.63879 14.2915 3.2783 13.877 2.99426C14.2915 2.71011 14.6474 2.34988 14.9258 1.93274Z" stroke="url(#paint4_linear_13000_10040)" stroke-width="2"/>
<path d="M14.9258 1.93274C15.204 2.34984 15.5602 2.71007 15.9746 2.99426C15.5603 3.27834 15.204 3.63883 14.9258 4.05579C14.6474 3.63879 14.2915 3.2783 13.877 2.99426C14.2915 2.71011 14.6474 2.34988 14.9258 1.93274Z" stroke="url(#paint5_linear_13000_10040)" stroke-width="2"/>
<defs>
<linearGradient id="paint0_linear_13000_10040" x1="15.3381" y1="1.62905" x2="-0.0728351" y2="5.16921" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13000_10040" x1="14.6632" y1="-7.06953" x2="-0.667868" y2="-6.22657" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13000_10040" x1="14.6632" y1="-7.06953" x2="-0.667868" y2="-6.22657" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint3_linear_13000_10040" x1="17.812" y1="0.336495" x2="11.9786" y2="1.80291" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint4_linear_13000_10040" x1="17.554" y1="-2.70214" x2="11.6968" y2="-2.34972" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint5_linear_13000_10040" x1="17.554" y1="-2.70214" x2="11.6968" y2="-2.34972" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    "sb_email-marketing": `<svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11.2652 5.58345L6.20407 9.98444M6.40814 11.019L6.60451 11.314C8.45884 14.0992 9.38601 15.4917 10.598 15.3743C11.8101 15.2568 12.4526 13.7121 13.7376 10.6228L15.0731 7.4123C16.5211 3.93106 17.2451 2.19045 16.3715 1.23492C15.4978 0.279395 13.6995 0.845016 10.1028 1.97626L5.59513 3.39402C2.72848 4.29565 1.29515 4.74647 0.923487 5.58711C0.738045 6.00654 0.699984 6.47639 0.815495 6.9202C1.047 7.8097 2.38908 8.48535 5.07323 9.83664C5.41498 10.0087 5.58586 10.0947 5.73596 10.2092C5.81477 10.2694 5.88907 10.3352 5.95827 10.4061C6.09007 10.5413 6.19609 10.7006 6.40814 11.019Z" stroke="url(#paint0_linear_13016_5775)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M11.2652 5.58345L6.20407 9.98444M6.40814 11.019L6.60451 11.314C8.45884 14.0992 9.38601 15.4917 10.598 15.3743C11.8101 15.2568 12.4526 13.7121 13.7376 10.6228L15.0731 7.4123C16.5211 3.93106 17.2451 2.19045 16.3715 1.23492C15.4978 0.279395 13.6995 0.845016 10.1028 1.97626L5.59513 3.39402C2.72848 4.29565 1.29515 4.74647 0.923487 5.58711C0.738045 6.00654 0.699984 6.47639 0.815495 6.9202C1.047 7.8097 2.38908 8.48535 5.07323 9.83664C5.41498 10.0087 5.58586 10.0947 5.73596 10.2092C5.81477 10.2694 5.88907 10.3352 5.95827 10.4061C6.09007 10.5413 6.19609 10.7006 6.40814 11.019Z" stroke="url(#paint1_linear_13016_5775)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M11.2652 5.58345L6.20407 9.98444M6.40814 11.019L6.60451 11.314C8.45884 14.0992 9.38601 15.4917 10.598 15.3743C11.8101 15.2568 12.4526 13.7121 13.7376 10.6228L15.0731 7.4123C16.5211 3.93106 17.2451 2.19045 16.3715 1.23492C15.4978 0.279395 13.6995 0.845016 10.1028 1.97626L5.59513 3.39402C2.72848 4.29565 1.29515 4.74647 0.923487 5.58711C0.738045 6.00654 0.699984 6.47639 0.815495 6.9202C1.047 7.8097 2.38908 8.48535 5.07323 9.83664C5.41498 10.0087 5.58586 10.0947 5.73596 10.2092C5.81477 10.2694 5.88907 10.3352 5.95827 10.4061C6.09007 10.5413 6.19609 10.7006 6.40814 11.019Z" stroke="url(#paint2_linear_13016_5775)" stroke-width="1.5" stroke-linecap="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5775" x1="17.5355" y1="0.936737" x2="0.0749143" y2="5.92014" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5775" x1="16.75" y1="-7.21178" x2="-1.06425" y2="-5.99482" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5775" x1="16.75" y1="-7.21178" x2="-1.06425" y2="-5.99482" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_automation: `<svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.59744 7.04174C8.44542 7.04174 8.32674 6.9103 8.34221 6.75907L8.92756 1.03564C8.95332 0.783736 8.63732 0.650756 8.47522 0.845279L0.811422 10.0418C0.672164 10.2089 0.790996 10.4627 1.00852 10.4627H6.0077C6.15972 10.4627 6.2784 10.5941 6.26294 10.7453L5.67759 16.4688C5.65182 16.7207 5.96782 16.8536 6.12992 16.6591L13.7937 7.46256C13.933 7.29545 13.8141 7.04174 13.5966 7.04174H8.59744Z" stroke="url(#paint0_linear_13016_5776)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M8.59744 7.04174C8.44542 7.04174 8.32674 6.9103 8.34221 6.75907L8.92756 1.03564C8.95332 0.783736 8.63732 0.650756 8.47522 0.845279L0.811422 10.0418C0.672164 10.2089 0.790996 10.4627 1.00852 10.4627H6.0077C6.15972 10.4627 6.2784 10.5941 6.26294 10.7453L5.67759 16.4688C5.65182 16.7207 5.96782 16.8536 6.12992 16.6591L13.7937 7.46256C13.933 7.29545 13.8141 7.04174 13.5966 7.04174H8.59744Z" stroke="url(#paint1_linear_13016_5776)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M8.59744 7.04174C8.44542 7.04174 8.32674 6.9103 8.34221 6.75907L8.92756 1.03564C8.95332 0.783736 8.63732 0.650756 8.47522 0.845279L0.811422 10.0418C0.672164 10.2089 0.790996 10.4627 1.00852 10.4627H6.0077C6.15972 10.4627 6.2784 10.5941 6.26294 10.7453L5.67759 16.4688C5.65182 16.7207 5.96782 16.8536 6.12992 16.6591L13.7937 7.46256C13.933 7.29545 13.8141 7.04174 13.5966 7.04174H8.59744Z" stroke="url(#paint2_linear_13016_5776)" stroke-width="1.5" stroke-linejoin="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5776" x1="14.4969" y1="0.9568" x2="-0.290579" y2="4.1173" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5776" x1="13.8537" y1="-7.95385" x2="-0.764023" y2="-7.20606" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5776" x1="13.8537" y1="-7.95385" x2="-0.764023" y2="-7.20606" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_sites: `<svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M16.75 6.75H0.75M3.75 3.75H3.76M6.75 3.75H6.76M9.75 3.75H9.76M1.63889 14.75H15.8611C16.352 14.75 16.75 14.352 16.75 13.8611V1.63889C16.75 1.14797 16.352 0.75 15.8611 0.75H1.63889C1.14797 0.75 0.75 1.14797 0.75 1.63889V13.8611C0.75 14.352 1.14797 14.75 1.63889 14.75Z" stroke="url(#paint0_linear_13016_5777)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M16.75 6.75H0.75M3.75 3.75H3.76M6.75 3.75H6.76M9.75 3.75H9.76M1.63889 14.75H15.8611C16.352 14.75 16.75 14.352 16.75 13.8611V1.63889C16.75 1.14797 16.352 0.75 15.8611 0.75H1.63889C1.14797 0.75 0.75 1.14797 0.75 1.63889V13.8611C0.75 14.352 1.14797 14.75 1.63889 14.75Z" stroke="url(#paint1_linear_13016_5777)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M16.75 6.75H0.75M3.75 3.75H3.76M6.75 3.75H6.76M9.75 3.75H9.76M1.63889 14.75H15.8611C16.352 14.75 16.75 14.352 16.75 13.8611V1.63889C16.75 1.14797 16.352 0.75 15.8611 0.75H1.63889C1.14797 0.75 0.75 1.14797 0.75 1.63889V13.8611C0.75 14.352 1.14797 14.75 1.63889 14.75Z" stroke="url(#paint2_linear_13016_5777)" stroke-width="1.5" stroke-linecap="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5777" x1="17.5355" y1="0.929027" x2="0.195404" y2="6.10128" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5777" x1="16.75" y1="-6.86779" x2="-1.05661" y2="-5.59648" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5777" x1="16.75" y1="-6.86779" x2="-1.05661" y2="-5.59648" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_memberships: `<svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.2774 7.28877L12.8723 2.74449C13.3798 1.85562 12.738 0.75 11.7144 0.75H10.2254C9.2684 0.75 8.38482 1.26276 7.91004 2.09363L6.90005 3.86111M3.524 7.28769L0.928564 2.74475C0.420739 1.85587 1.06257 0.75 2.08628 0.75H3.57474C4.5317 0.75 5.41528 1.26276 5.89006 2.09363L6.90005 3.86111M6.90005 3.86111L8.27161 6.26135M8.27161 6.26135C7.83397 6.14522 7.37424 6.08333 6.90005 6.08333C3.95453 6.08333 1.56672 8.47115 1.56672 11.4167C1.56672 14.3622 3.95453 16.75 6.90005 16.75C9.84557 16.75 12.2334 14.3622 12.2334 11.4167C12.2334 8.94534 10.5525 6.8666 8.27161 6.26135ZM7.78894 11.4167C7.78894 11.9076 7.39097 12.3056 6.90005 12.3056C6.40913 12.3056 6.01116 11.9076 6.01116 11.4167C6.01116 10.9257 6.40913 10.5278 6.90005 10.5278C7.39097 10.5278 7.78894 10.9257 7.78894 11.4167Z" stroke="url(#paint0_linear_13016_5760)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M10.2774 7.28877L12.8723 2.74449C13.3798 1.85562 12.738 0.75 11.7144 0.75H10.2254C9.2684 0.75 8.38482 1.26276 7.91004 2.09363L6.90005 3.86111M3.524 7.28769L0.928564 2.74475C0.420739 1.85587 1.06257 0.75 2.08628 0.75H3.57474C4.5317 0.75 5.41528 1.26276 5.89006 2.09363L6.90005 3.86111M6.90005 3.86111L8.27161 6.26135M8.27161 6.26135C7.83397 6.14522 7.37424 6.08333 6.90005 6.08333C3.95453 6.08333 1.56672 8.47115 1.56672 11.4167C1.56672 14.3622 3.95453 16.75 6.90005 16.75C9.84557 16.75 12.2334 14.3622 12.2334 11.4167C12.2334 8.94534 10.5525 6.8666 8.27161 6.26135ZM7.78894 11.4167C7.78894 11.9076 7.39097 12.3056 6.90005 12.3056C6.40913 12.3056 6.01116 11.9076 6.01116 11.4167C6.01116 10.9257 6.40913 10.5278 6.90005 10.5278C7.39097 10.5278 7.78894 10.9257 7.78894 11.4167Z" stroke="url(#paint1_linear_13016_5760)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M10.2774 7.28877L12.8723 2.74449C13.3798 1.85562 12.738 0.75 11.7144 0.75H10.2254C9.2684 0.75 8.38482 1.26276 7.91004 2.09363L6.90005 3.86111M3.524 7.28769L0.928564 2.74475C0.420739 1.85587 1.06257 0.75 2.08628 0.75H3.57474C4.5317 0.75 5.41528 1.26276 5.89006 2.09363L6.90005 3.86111M6.90005 3.86111L8.27161 6.26135M8.27161 6.26135C7.83397 6.14522 7.37424 6.08333 6.90005 6.08333C3.95453 6.08333 1.56672 8.47115 1.56672 11.4167C1.56672 14.3622 3.95453 16.75 6.90005 16.75C9.84557 16.75 12.2334 14.3622 12.2334 11.4167C12.2334 8.94534 10.5525 6.8666 8.27161 6.26135ZM7.78894 11.4167C7.78894 11.9076 7.39097 12.3056 6.90005 12.3056C6.40913 12.3056 6.01116 11.9076 6.01116 11.4167C6.01116 10.9257 6.40913 10.5278 6.90005 10.5278C7.39097 10.5278 7.78894 10.9257 7.78894 11.4167Z" stroke="url(#paint2_linear_13016_5760)" stroke-width="1.5" stroke-linecap="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5760" x1="13.6535" y1="0.954602" x2="-0.29963" y2="3.7539" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5760" x1="13.0497" y1="-7.95605" x2="-0.675835" y2="-7.29695" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5760" x1="13.0497" y1="-7.95605" x2="-0.675835" y2="-7.29695" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    "sb_app-media": `<svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.42601 14.3971L9.02576 8.79731C10.2807 7.54241 10.9081 6.91496 11.6878 6.91496C12.4675 6.91496 13.095 7.54241 14.3499 8.79731L16.2795 10.727M5.92647 5.45588C5.92647 5.71578 5.71578 5.92647 5.45588 5.92647C5.19598 5.92647 4.98529 5.71578 4.98529 5.45588C4.98529 5.19598 5.19598 4.98529 5.45588 4.98529C5.71578 4.98529 5.92647 5.19598 5.92647 5.45588ZM6.39706 14.8676H11.1029C13.765 14.8676 15.096 14.8676 15.923 14.0407C16.75 13.2137 16.75 11.8826 16.75 9.22059V6.39706C16.75 3.73501 16.75 2.40399 15.923 1.57699C15.096 0.75 13.765 0.75 11.1029 0.75H6.39706C3.73501 0.75 2.40399 0.75 1.57699 1.57699C0.75 2.40399 0.75 3.73501 0.75 6.39706V9.22059C0.75 11.8826 0.75 13.2137 1.57699 14.0407C2.40399 14.8676 3.73501 14.8676 6.39706 14.8676Z" stroke="url(#paint0_linear_13016_5759)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M3.42601 14.3971L9.02576 8.79731C10.2807 7.54241 10.9081 6.91496 11.6878 6.91496C12.4675 6.91496 13.095 7.54241 14.3499 8.79731L16.2795 10.727M5.92647 5.45588C5.92647 5.71578 5.71578 5.92647 5.45588 5.92647C5.19598 5.92647 4.98529 5.71578 4.98529 5.45588C4.98529 5.19598 5.19598 4.98529 5.45588 4.98529C5.71578 4.98529 5.92647 5.19598 5.92647 5.45588ZM6.39706 14.8676H11.1029C13.765 14.8676 15.096 14.8676 15.923 14.0407C16.75 13.2137 16.75 11.8826 16.75 9.22059V6.39706C16.75 3.73501 16.75 2.40399 15.923 1.57699C15.096 0.75 13.765 0.75 11.1029 0.75H6.39706C3.73501 0.75 2.40399 0.75 1.57699 1.57699C0.75 2.40399 0.75 3.73501 0.75 6.39706V9.22059C0.75 11.8826 0.75 13.2137 1.57699 14.0407C2.40399 14.8676 3.73501 14.8676 6.39706 14.8676Z" stroke="url(#paint1_linear_13016_5759)" stroke-width="1.5" stroke-linecap="round"/>
<path d="M3.42601 14.3971L9.02576 8.79731C10.2807 7.54241 10.9081 6.91496 11.6878 6.91496C12.4675 6.91496 13.095 7.54241 14.3499 8.79731L16.2795 10.727M5.92647 5.45588C5.92647 5.71578 5.71578 5.92647 5.45588 5.92647C5.19598 5.92647 4.98529 5.71578 4.98529 5.45588C4.98529 5.19598 5.19598 4.98529 5.45588 4.98529C5.71578 4.98529 5.92647 5.19598 5.92647 5.45588ZM6.39706 14.8676H11.1029C13.765 14.8676 15.096 14.8676 15.923 14.0407C16.75 13.2137 16.75 11.8826 16.75 9.22059V6.39706C16.75 3.73501 16.75 2.40399 15.923 1.57699C15.096 0.75 13.765 0.75 11.1029 0.75H6.39706C3.73501 0.75 2.40399 0.75 1.57699 1.57699C0.75 2.40399 0.75 3.73501 0.75 6.39706V9.22059C0.75 11.8826 0.75 13.2137 1.57699 14.0407C2.40399 14.8676 3.73501 14.8676 6.39706 14.8676Z" stroke="url(#paint2_linear_13016_5759)" stroke-width="1.5" stroke-linecap="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5759" x1="17.5355" y1="0.930532" x2="0.171858" y2="6.06665" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5759" x1="16.75" y1="-6.93181" x2="-1.05811" y2="-5.67099" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5759" x1="16.75" y1="-6.93181" x2="-1.05811" y2="-5.67099" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_reputation: `<svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.5432 0.869151C8.63573 0.710283 8.86524 0.710283 8.95778 0.869151L11.4891 5.21518C11.523 5.27338 11.5798 5.31465 11.6456 5.3289L16.5612 6.39335C16.7409 6.43226 16.8118 6.65053 16.6893 6.78763L13.3382 10.5381C13.2933 10.5883 13.2716 10.6551 13.2784 10.7221L13.785 15.726C13.8036 15.9089 13.6179 16.0438 13.4496 15.9696L8.84722 13.9415C8.78559 13.9144 8.71538 13.9144 8.65375 13.9415L4.05133 15.9696C3.88309 16.0438 3.69741 15.9089 3.71593 15.726L4.22257 10.7221C4.22935 10.6551 4.20766 10.5883 4.16279 10.5381L0.811688 6.78763C0.68919 6.65053 0.760113 6.43226 0.9398 6.39335L5.85535 5.3289C5.92117 5.31465 5.97797 5.27338 6.01187 5.21518L8.5432 0.869151Z" stroke="url(#paint0_linear_13016_5778)" stroke-width="1.5"/>
<path d="M8.5432 0.869151C8.63573 0.710283 8.86524 0.710283 8.95778 0.869151L11.4891 5.21518C11.523 5.27338 11.5798 5.31465 11.6456 5.3289L16.5612 6.39335C16.7409 6.43226 16.8118 6.65053 16.6893 6.78763L13.3382 10.5381C13.2933 10.5883 13.2716 10.6551 13.2784 10.7221L13.785 15.726C13.8036 15.9089 13.6179 16.0438 13.4496 15.9696L8.84722 13.9415C8.78559 13.9144 8.71538 13.9144 8.65375 13.9415L4.05133 15.9696C3.88309 16.0438 3.69741 15.9089 3.71593 15.726L4.22257 10.7221C4.22935 10.6551 4.20766 10.5883 4.16279 10.5381L0.811688 6.78763C0.68919 6.65053 0.760113 6.43226 0.9398 6.39335L5.85535 5.3289C5.92117 5.31465 5.97797 5.27338 6.01187 5.21518L8.5432 0.869151Z" stroke="url(#paint1_linear_13016_5778)" stroke-width="1.5"/>
<path d="M8.5432 0.869151C8.63573 0.710283 8.86524 0.710283 8.95778 0.869151L11.4891 5.21518C11.523 5.27338 11.5798 5.31465 11.6456 5.3289L16.5612 6.39335C16.7409 6.43226 16.8118 6.65053 16.6893 6.78763L13.3382 10.5381C13.2933 10.5883 13.2716 10.6551 13.2784 10.7221L13.785 15.726C13.8036 15.9089 13.6179 16.0438 13.4496 15.9696L8.84722 13.9415C8.78559 13.9144 8.71538 13.9144 8.65375 13.9415L4.05133 15.9696C3.88309 16.0438 3.69741 15.9089 3.71593 15.726L4.22257 10.7221C4.22935 10.6551 4.20766 10.5883 4.16279 10.5381L0.811688 6.78763C0.68919 6.65053 0.760113 6.43226 0.9398 6.39335L5.85535 5.3289C5.92117 5.31465 5.97797 5.27338 6.01187 5.21518L8.5432 0.869151Z" stroke="url(#paint2_linear_13016_5778)" stroke-width="1.5"/>
<defs>
<linearGradient id="paint0_linear_13016_5778" x1="17.5359" y1="0.944889" x2="-0.0281925" y2="5.75759" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5778" x1="16.7505" y1="-7.54271" x2="-1.07024" y2="-6.37395" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5778" x1="16.7505" y1="-7.54271" x2="-1.07024" y2="-6.37395" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`,
    sb_settings: `<svg width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M17.3388 3.18889C17.3582 2.85088 17.2453 2.51049 16.9807 2.29929C16.2094 1.6837 15.3451 1.17988 14.4123 0.812312C14.0967 0.687953 13.7447 0.760535 13.4612 0.946903C12.6593 1.4742 11.7346 1.78774 10.75 1.78774C9.76544 1.78774 8.8407 1.4742 8.03876 0.946903C7.75532 0.760535 7.40328 0.687953 7.08768 0.812312C6.15483 1.17989 5.29052 1.68374 4.51923 2.29936C4.25463 2.51055 4.14168 2.85093 4.1611 3.18892C4.21617 4.147 4.02508 5.10457 3.53307 5.95733C3.041 6.81022 2.30768 7.45467 1.45083 7.886C1.14773 8.03858 0.909019 8.30747 0.859163 8.64313C0.787256 9.12726 0.75 9.6227 0.75 10.1268C0.75 10.631 0.787259 11.1265 0.859172 11.6106C0.909029 11.9463 1.14773 12.2152 1.45084 12.3677C2.30769 12.7991 3.041 13.4435 3.53307 14.2964C4.02507 15.1492 4.21616 16.1067 4.1611 17.0648C4.14168 17.4028 4.25463 17.7431 4.51923 17.9543C5.2905 18.5699 6.15478 19.0738 7.08758 19.4413C7.4032 19.5657 7.75526 19.4931 8.0387 19.3067C8.84065 18.7794 9.76541 18.4658 10.75 18.4658C11.7346 18.4658 12.6594 18.7794 13.4613 19.3067C13.7447 19.4931 14.0968 19.5657 14.4124 19.4413C15.3452 19.0738 16.2094 18.57 16.9807 17.9544C17.2453 17.7432 17.3582 17.4028 17.3388 17.0648C17.2837 16.1067 17.4748 15.1492 17.9668 14.2964C18.4589 13.4435 19.1922 12.799 20.0491 12.3677C20.3523 12.2151 20.591 11.9463 20.6408 11.6106C20.7127 11.1264 20.75 10.631 20.75 10.1268C20.75 9.62276 20.7128 9.12739 20.6409 8.64333C20.591 8.30765 20.3523 8.03874 20.0492 7.88616C19.1923 7.45485 18.4589 6.81039 17.9668 5.95746C17.4748 5.10466 17.2837 4.14703 17.3388 3.18889Z" stroke="url(#paint0_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M17.3388 3.18889C17.3582 2.85088 17.2453 2.51049 16.9807 2.29929C16.2094 1.6837 15.3451 1.17988 14.4123 0.812312C14.0967 0.687953 13.7447 0.760535 13.4612 0.946903C12.6593 1.4742 11.7346 1.78774 10.75 1.78774C9.76544 1.78774 8.8407 1.4742 8.03876 0.946903C7.75532 0.760535 7.40328 0.687953 7.08768 0.812312C6.15483 1.17989 5.29052 1.68374 4.51923 2.29936C4.25463 2.51055 4.14168 2.85093 4.1611 3.18892C4.21617 4.147 4.02508 5.10457 3.53307 5.95733C3.041 6.81022 2.30768 7.45467 1.45083 7.886C1.14773 8.03858 0.909019 8.30747 0.859163 8.64313C0.787256 9.12726 0.75 9.6227 0.75 10.1268C0.75 10.631 0.787259 11.1265 0.859172 11.6106C0.909029 11.9463 1.14773 12.2152 1.45084 12.3677C2.30769 12.7991 3.041 13.4435 3.53307 14.2964C4.02507 15.1492 4.21616 16.1067 4.1611 17.0648C4.14168 17.4028 4.25463 17.7431 4.51923 17.9543C5.2905 18.5699 6.15478 19.0738 7.08758 19.4413C7.4032 19.5657 7.75526 19.4931 8.0387 19.3067C8.84065 18.7794 9.76541 18.4658 10.75 18.4658C11.7346 18.4658 12.6594 18.7794 13.4613 19.3067C13.7447 19.4931 14.0968 19.5657 14.4124 19.4413C15.3452 19.0738 16.2094 18.57 16.9807 17.9544C17.2453 17.7432 17.3582 17.4028 17.3388 17.0648C17.2837 16.1067 17.4748 15.1492 17.9668 14.2964C18.4589 13.4435 19.1922 12.799 20.0491 12.3677C20.3523 12.2151 20.591 11.9463 20.6408 11.6106C20.7127 11.1264 20.75 10.631 20.75 10.1268C20.75 9.62276 20.7128 9.12739 20.6409 8.64333C20.591 8.30765 20.3523 8.03874 20.0492 7.88616C19.1923 7.45485 18.4589 6.81039 17.9668 5.95746C17.4748 5.10466 17.2837 4.14703 17.3388 3.18889Z" stroke="url(#paint1_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M17.3388 3.18889C17.3582 2.85088 17.2453 2.51049 16.9807 2.29929C16.2094 1.6837 15.3451 1.17988 14.4123 0.812312C14.0967 0.687953 13.7447 0.760535 13.4612 0.946903C12.6593 1.4742 11.7346 1.78774 10.75 1.78774C9.76544 1.78774 8.8407 1.4742 8.03876 0.946903C7.75532 0.760535 7.40328 0.687953 7.08768 0.812312C6.15483 1.17989 5.29052 1.68374 4.51923 2.29936C4.25463 2.51055 4.14168 2.85093 4.1611 3.18892C4.21617 4.147 4.02508 5.10457 3.53307 5.95733C3.041 6.81022 2.30768 7.45467 1.45083 7.886C1.14773 8.03858 0.909019 8.30747 0.859163 8.64313C0.787256 9.12726 0.75 9.6227 0.75 10.1268C0.75 10.631 0.787259 11.1265 0.859172 11.6106C0.909029 11.9463 1.14773 12.2152 1.45084 12.3677C2.30769 12.7991 3.041 13.4435 3.53307 14.2964C4.02507 15.1492 4.21616 16.1067 4.1611 17.0648C4.14168 17.4028 4.25463 17.7431 4.51923 17.9543C5.2905 18.5699 6.15478 19.0738 7.08758 19.4413C7.4032 19.5657 7.75526 19.4931 8.0387 19.3067C8.84065 18.7794 9.76541 18.4658 10.75 18.4658C11.7346 18.4658 12.6594 18.7794 13.4613 19.3067C13.7447 19.4931 14.0968 19.5657 14.4124 19.4413C15.3452 19.0738 16.2094 18.57 16.9807 17.9544C17.2453 17.7432 17.3582 17.4028 17.3388 17.0648C17.2837 16.1067 17.4748 15.1492 17.9668 14.2964C18.4589 13.4435 19.1922 12.799 20.0491 12.3677C20.3523 12.2151 20.591 11.9463 20.6408 11.6106C20.7127 11.1264 20.75 10.631 20.75 10.1268C20.75 9.62276 20.7128 9.12739 20.6409 8.64333C20.591 8.30765 20.3523 8.03874 20.0492 7.88616C19.1923 7.45485 18.4589 6.81039 17.9668 5.95746C17.4748 5.10466 17.2837 4.14703 17.3388 3.18889Z" stroke="url(#paint2_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M14.0833 10.1261C14.0833 11.9684 12.5909 13.4618 10.75 13.4618C8.90905 13.4618 7.41667 11.9684 7.41667 10.1261C7.41667 8.28391 8.90905 6.7905 10.75 6.7905C12.5909 6.7905 14.0833 8.28391 14.0833 10.1261Z" stroke="url(#paint3_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M14.0833 10.1261C14.0833 11.9684 12.5909 13.4618 10.75 13.4618C8.90905 13.4618 7.41667 11.9684 7.41667 10.1261C7.41667 8.28391 8.90905 6.7905 10.75 6.7905C12.5909 6.7905 14.0833 8.28391 14.0833 10.1261Z" stroke="url(#paint4_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M14.0833 10.1261C14.0833 11.9684 12.5909 13.4618 10.75 13.4618C8.90905 13.4618 7.41667 11.9684 7.41667 10.1261C7.41667 8.28391 8.90905 6.7905 10.75 6.7905C12.5909 6.7905 14.0833 8.28391 14.0833 10.1261Z" stroke="url(#paint5_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<defs>
<linearGradient id="paint0_linear_12991_9775" x1="21.7318" y1="0.869796" x2="-0.215198" y2="6.89941" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_12991_9775" x1="20.75" y1="-9.7116" x2="-1.5254" y2="-8.2468" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_12991_9775" x1="20.75" y1="-9.7116" x2="-1.5254" y2="-8.2468" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint3_linear_12991_9775" x1="21.7318" y1="0.869796" x2="-0.215198" y2="6.89941" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint4_linear_12991_9775" x1="20.75" y1="-9.7116" x2="-1.5254" y2="-8.2468" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint5_linear_12991_9775" x1="20.75" y1="-9.7116" x2="-1.5254" y2="-8.2468" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>

`,
    sb_reporting: `<svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M6.08333 14.9722V8.75H1.63889C1.14797 8.75 0.75 9.14797 0.75 9.63889V14.0833C0.75 14.5743 1.14797 14.9722 1.63889 14.9722H6.08333ZM6.08333 14.9722H11.4167M6.08333 14.9722V6.08333C6.08333 5.59241 6.4813 5.19444 6.97222 5.19444H11.4167V14.9722M11.4167 14.9722H15.8611C16.352 14.9722 16.75 14.5743 16.75 14.0833V1.63889C16.75 1.14797 16.352 0.75 15.8611 0.75H12.3056C11.8146 0.75 11.4167 1.14797 11.4167 1.63889V14.9722Z" stroke="url(#paint0_linear_13016_5779)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M6.08333 14.9722V8.75H1.63889C1.14797 8.75 0.75 9.14797 0.75 9.63889V14.0833C0.75 14.5743 1.14797 14.9722 1.63889 14.9722H6.08333ZM6.08333 14.9722H11.4167M6.08333 14.9722V6.08333C6.08333 5.59241 6.4813 5.19444 6.97222 5.19444H11.4167V14.9722M11.4167 14.9722H15.8611C16.352 14.9722 16.75 14.5743 16.75 14.0833V1.63889C16.75 1.14797 16.352 0.75 15.8611 0.75H12.3056C11.8146 0.75 11.4167 1.14797 11.4167 1.63889V14.9722Z" stroke="url(#paint1_linear_13016_5779)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M6.08333 14.9722V8.75H1.63889C1.14797 8.75 0.75 9.14797 0.75 9.63889V14.0833C0.75 14.5743 1.14797 14.9722 1.63889 14.9722H6.08333ZM6.08333 14.9722H11.4167M6.08333 14.9722V6.08333C6.08333 5.59241 6.4813 5.19444 6.97222 5.19444H11.4167V14.9722M11.4167 14.9722H15.8611C16.352 14.9722 16.75 14.5743 16.75 14.0833V1.63889C16.75 1.14797 16.352 0.75 15.8611 0.75H12.3056C11.8146 0.75 11.4167 1.14797 11.4167 1.63889V14.9722Z" stroke="url(#paint2_linear_13016_5779)" stroke-width="1.5" stroke-linejoin="round"/>
<defs>
<linearGradient id="paint0_linear_13016_5779" x1="17.5355" y1="0.931869" x2="0.151365" y2="6.03624" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_13016_5779" x1="16.75" y1="-6.98871" x2="-1.05942" y2="-5.73707" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_13016_5779" x1="16.75" y1="-6.98871" x2="-1.05942" y2="-5.73707" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg>
`
  };
  let iconObserver;

  function processIcons() {
    if (iconObserver) iconObserver.disconnect();

    const path = window.location.pathname;

    Object.keys(iconMap).forEach(id => {
      let anchor = document.getElementById(id);
      if (!anchor && id === "sb_tasks") {
        anchor = document.querySelector('a[data-href*="/tasks"], a[href*="/tasks"]');
      }

      if (anchor) {
        const existingIcon = anchor.querySelector("img, i");
        if (existingIcon && existingIcon.style.display !== "none") {
          existingIcon.style.setProperty("display", "none", "important");
        }

        if (!anchor.querySelector("svg")) {
          anchor.insertAdjacentHTML("afterbegin", iconMap[id]);
        }
      }
    });

    const tasksLink = document.querySelector('a[data-href*="/tasks"], a[href*="/tasks"]');
    const contactsLink = document.getElementById("sb_contacts");

    if (path.includes("/tasks") || path.includes("/businesses")) {
      if (contactsLink && contactsLink.classList.contains("exact-active")) {
        contactsLink.classList.remove("exact-active", "active");
      }
      if (tasksLink && !tasksLink.classList.contains("exact-active")) {
        tasksLink.classList.add("exact-active");
      }
    } else {
      if (tasksLink && tasksLink.classList.contains("exact-active")) {
        tasksLink.classList.remove("exact-active", "active");
      }
    }

    const conversationsLink = document.querySelector('a[data-href*="/conversations"], a[href*="/conversations"]');

    if (path.includes("/conversations")) {
      if (contactsLink && contactsLink.classList.contains("exact-active")) {
        contactsLink.classList.remove("exact-active", "active");
      }
      if (conversationsLink && !conversationsLink.classList.contains("exact-active")) {
        conversationsLink.classList.add("exact-active");
      }
    } else {
      if (conversationsLink && conversationsLink.classList.contains("exact-active")) {
        conversationsLink.classList.remove("exact-active", "active");
      }
    }

    const sitesLink = document.querySelector('a[data-href*="/wordpress"], a[href*="/wordpress"]');
    if (path.includes("/wordpress")) {
      if (sitesLink && !sitesLink.classList.contains("exact-active")) {
        sitesLink.classList.add("exact-active");
      }
    } else {
      if (sitesLink && sitesLink.classList.contains("exact-active")) {
        sitesLink.classList.remove("exact-active", "active");
      }
    }

    const reputationLink = document.getElementById("sb_reputation");
    if (path.includes("/reputation/")) {
      if (reputationLink && !reputationLink.classList.contains("exact-active")) {
        reputationLink.classList.add("exact-active");
      }
    } else {
      if (reputationLink && reputationLink.classList.contains("exact-active") && !path.includes("/reputation")) {
        reputationLink.classList.remove("exact-active");
      }
    }

    if (!document.querySelector(".hl-top-wrapper")) {
      let hlHeader = document.querySelector(".hl_header");
      if (hlHeader) hlHeader.classList.add("hl-top-wrapper");
    }
    if (document.querySelectorAll(".hl-top-wrapper").length > 1) {
      if (document.querySelector(".hl_header.hl-top-wrapper")) {
        document.querySelector(".hl_header.hl-top-wrapper").classList.remove("hl-top-wrapper");
      }
    }

    const topMenu = document.getElementById("tb_calendar-settings-top");
    const activeItem = document.querySelector(".hl_nav-header .exact-active");

    if (topMenu && activeItem) {
      const activeSvg = activeItem.querySelector("svg");
      const activeText = activeItem.querySelector(".nav-title");
      const targetSpan = topMenu.querySelector("span");

      if (activeSvg && targetSpan && !topMenu.classList.contains("injected")) {
        targetSpan.innerHTML = "";
        targetSpan.appendChild(activeSvg.cloneNode(true));
        const text = activeText ? activeText.innerText.trim() : "Menu";
        targetSpan.insertAdjacentText("beforeend", ` ${text} Settings`);
        topMenu.classList.add("injected");
      }
    }

    if (iconObserver) {
      iconObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"]
      });
    }
  }

  iconObserver = new MutationObserver(() => {
    processIcons();
  });

  function start() {
    setTimeout(() => {
      processIcons();
      setInterval(processIcons, 100);
    }, 100);
  }

  if (document.readyState === "complete") {
    start();
  } else {
    window.addEventListener("load", start);
  }
})();

(function () {
  function fixContactsLink() {
    const contactsBtn = document.getElementById("sb_contacts");
    if (!contactsBtn) return;

    const currentHref = contactsBtn.getAttribute("href") || "";

    // If GHL accidentally pointed it to /tasks, intercept it
    if (currentHref.includes("/tasks")) {
      const match = window.location.pathname.match(/\/location\/([^/]+)/);
      if (match) {
        const locationId = match[1];
        const correctHref = `/v2/location/${locationId}/contacts/smart_list/All`;

        // 1. Clone the node. This strips ALL hidden Vue/event listeners natively!
        const cleanBtn = contactsBtn.cloneNode(true);

        // 2. Remove any inline onclick attributes just to be safe
        cleanBtn.removeAttribute("onclick");
        cleanBtn.onclick = null;

        // 3. Apply the correct URL
        cleanBtn.setAttribute("href", correctHref);

        // 4. Swap the old button out for our clean clone
        contactsBtn.parentNode.replaceChild(cleanBtn, contactsBtn);
      }
    }
  }

  // Run immediately
  fixContactsLink();

  // Watch the DOM for changes
  const sidebarObserver = new MutationObserver(() => {
    fixContactsLink();
  });

  sidebarObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
})();

// 1. Define the function you want to run
function handleSettingsTopNav() {
  if (isPaymentsIntegrationsDashboardRoute()) return;

  let existingIconContainer = document.getElementById("hl-active-icon-container");
  let headerContainer = document.querySelector(".hl_header");
  if (headerContainer && !existingIconContainer && checkIsSettingsPage()) {
    headerContainer.insertAdjacentHTML(
      "afterbegin",
      `
      <div id="hl-active-icon-container"><svg width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M17.3388 3.18889C17.3582 2.85088 17.2453 2.51049 16.9807 2.29929C16.2094 1.6837 15.3451 1.17988 14.4123 0.812312C14.0967 0.687953 13.7447 0.760535 13.4612 0.946903C12.6593 1.4742 11.7346 1.78774 10.75 1.78774C9.76544 1.78774 8.8407 1.4742 8.03876 0.946903C7.75532 0.760535 7.40328 0.687953 7.08768 0.812312C6.15483 1.17989 5.29052 1.68374 4.51923 2.29936C4.25463 2.51055 4.14168 2.85093 4.1611 3.18892C4.21617 4.147 4.02508 5.10457 3.53307 5.95733C3.041 6.81022 2.30768 7.45467 1.45083 7.886C1.14773 8.03858 0.909019 8.30747 0.859163 8.64313C0.787256 9.12726 0.75 9.6227 0.75 10.1268C0.75 10.631 0.787259 11.1265 0.859172 11.6106C0.909029 11.9463 1.14773 12.2152 1.45084 12.3677C2.30769 12.7991 3.041 13.4435 3.53307 14.2964C4.02507 15.1492 4.21616 16.1067 4.1611 17.0648C4.14168 17.4028 4.25463 17.7431 4.51923 17.9543C5.2905 18.5699 6.15478 19.0738 7.08758 19.4413C7.4032 19.5657 7.75526 19.4931 8.0387 19.3067C8.84065 18.7794 9.76541 18.4658 10.75 18.4658C11.7346 18.4658 12.6594 18.7794 13.4613 19.3067C13.7447 19.4931 14.0968 19.5657 14.4124 19.4413C15.3452 19.0738 16.2094 18.57 16.9807 17.9544C17.2453 17.7432 17.3582 17.4028 17.3388 17.0648C17.2837 16.1067 17.4748 15.1492 17.9668 14.2964C18.4589 13.4435 19.1922 12.799 20.0491 12.3677C20.3523 12.2151 20.591 11.9463 20.6408 11.6106C20.7127 11.1264 20.75 10.631 20.75 10.1268C20.75 9.62276 20.7128 9.12739 20.6409 8.64333C20.591 8.30765 20.3523 8.03874 20.0492 7.88616C19.1923 7.45485 18.4589 6.81039 17.9668 5.95746C17.4748 5.10466 17.2837 4.14703 17.3388 3.18889Z" stroke="url(#paint0_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M17.3388 3.18889C17.3582 2.85088 17.2453 2.51049 16.9807 2.29929C16.2094 1.6837 15.3451 1.17988 14.4123 0.812312C14.0967 0.687953 13.7447 0.760535 13.4612 0.946903C12.6593 1.4742 11.7346 1.78774 10.75 1.78774C9.76544 1.78774 8.8407 1.4742 8.03876 0.946903C7.75532 0.760535 7.40328 0.687953 7.08768 0.812312C6.15483 1.17989 5.29052 1.68374 4.51923 2.29936C4.25463 2.51055 4.14168 2.85093 4.1611 3.18892C4.21617 4.147 4.02508 5.10457 3.53307 5.95733C3.041 6.81022 2.30768 7.45467 1.45083 7.886C1.14773 8.03858 0.909019 8.30747 0.859163 8.64313C0.787256 9.12726 0.75 9.6227 0.75 10.1268C0.75 10.631 0.787259 11.1265 0.859172 11.6106C0.909029 11.9463 1.14773 12.2152 1.45084 12.3677C2.30769 12.7991 3.041 13.4435 3.53307 14.2964C4.02507 15.1492 4.21616 16.1067 4.1611 17.0648C4.14168 17.4028 4.25463 17.7431 4.51923 17.9543C5.2905 18.5699 6.15478 19.0738 7.08758 19.4413C7.4032 19.5657 7.75526 19.4931 8.0387 19.3067C8.84065 18.7794 9.76541 18.4658 10.75 18.4658C11.7346 18.4658 12.6594 18.7794 13.4613 19.3067C13.7447 19.4931 14.0968 19.5657 14.4124 19.4413C15.3452 19.0738 16.2094 18.57 16.9807 17.9544C17.2453 17.7432 17.3582 17.4028 17.3388 17.0648C17.2837 16.1067 17.4748 15.1492 17.9668 14.2964C18.4589 13.4435 19.1922 12.799 20.0491 12.3677C20.3523 12.2151 20.591 11.9463 20.6408 11.6106C20.7127 11.1264 20.75 10.631 20.75 10.1268C20.75 9.62276 20.7128 9.12739 20.6409 8.64333C20.591 8.30765 20.3523 8.03874 20.0492 7.88616C19.1923 7.45485 18.4589 6.81039 17.9668 5.95746C17.4748 5.10466 17.2837 4.14703 17.3388 3.18889Z" stroke="url(#paint1_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M17.3388 3.18889C17.3582 2.85088 17.2453 2.51049 16.9807 2.29929C16.2094 1.6837 15.3451 1.17988 14.4123 0.812312C14.0967 0.687953 13.7447 0.760535 13.4612 0.946903C12.6593 1.4742 11.7346 1.78774 10.75 1.78774C9.76544 1.78774 8.8407 1.4742 8.03876 0.946903C7.75532 0.760535 7.40328 0.687953 7.08768 0.812312C6.15483 1.17989 5.29052 1.68374 4.51923 2.29936C4.25463 2.51055 4.14168 2.85093 4.1611 3.18892C4.21617 4.147 4.02508 5.10457 3.53307 5.95733C3.041 6.81022 2.30768 7.45467 1.45083 7.886C1.14773 8.03858 0.909019 8.30747 0.859163 8.64313C0.787256 9.12726 0.75 9.6227 0.75 10.1268C0.75 10.631 0.787259 11.1265 0.859172 11.6106C0.909029 11.9463 1.14773 12.2152 1.45084 12.3677C2.30769 12.7991 3.041 13.4435 3.53307 14.2964C4.02507 15.1492 4.21616 16.1067 4.1611 17.0648C4.14168 17.4028 4.25463 17.7431 4.51923 17.9543C5.2905 18.5699 6.15478 19.0738 7.08758 19.4413C7.4032 19.5657 7.75526 19.4931 8.0387 19.3067C8.84065 18.7794 9.76541 18.4658 10.75 18.4658C11.7346 18.4658 12.6594 18.7794 13.4613 19.3067C13.7447 19.4931 14.0968 19.5657 14.4124 19.4413C15.3452 19.0738 16.2094 18.57 16.9807 17.9544C17.2453 17.7432 17.3582 17.4028 17.3388 17.0648C17.2837 16.1067 17.4748 15.1492 17.9668 14.2964C18.4589 13.4435 19.1922 12.799 20.0491 12.3677C20.3523 12.2151 20.591 11.9463 20.6408 11.6106C20.7127 11.1264 20.75 10.631 20.75 10.1268C20.75 9.62276 20.7128 9.12739 20.6409 8.64333C20.591 8.30765 20.3523 8.03874 20.0492 7.88616C19.1923 7.45485 18.4589 6.81039 17.9668 5.95746C17.4748 5.10466 17.2837 4.14703 17.3388 3.18889Z" stroke="url(#paint2_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M14.0833 10.1261C14.0833 11.9684 12.5909 13.4618 10.75 13.4618C8.90905 13.4618 7.41667 11.9684 7.41667 10.1261C7.41667 8.28391 8.90905 6.7905 10.75 6.7905C12.5909 6.7905 14.0833 8.28391 14.0833 10.1261Z" stroke="url(#paint3_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M14.0833 10.1261C14.0833 11.9684 12.5909 13.4618 10.75 13.4618C8.90905 13.4618 7.41667 11.9684 7.41667 10.1261C7.41667 8.28391 8.90905 6.7905 10.75 6.7905C12.5909 6.7905 14.0833 8.28391 14.0833 10.1261Z" stroke="url(#paint4_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M14.0833 10.1261C14.0833 11.9684 12.5909 13.4618 10.75 13.4618C8.90905 13.4618 7.41667 11.9684 7.41667 10.1261C7.41667 8.28391 8.90905 6.7905 10.75 6.7905C12.5909 6.7905 14.0833 8.28391 14.0833 10.1261Z" stroke="url(#paint5_linear_12991_9775)" stroke-width="1.5" stroke-linejoin="round"/>
<defs>
<linearGradient id="paint0_linear_12991_9775" x1="21.7318" y1="0.869796" x2="-0.215198" y2="6.89941" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint1_linear_12991_9775" x1="20.75" y1="-9.7116" x2="-1.5254" y2="-8.2468" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint2_linear_12991_9775" x1="20.75" y1="-9.7116" x2="-1.5254" y2="-8.2468" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint3_linear_12991_9775" x1="21.7318" y1="0.869796" x2="-0.215198" y2="6.89941" gradientUnits="userSpaceOnUse">
<stop stop-color="#EA1877"/>
<stop offset="1" stop-color="#FF8732"/>
</linearGradient>
<linearGradient id="paint4_linear_12991_9775" x1="20.75" y1="-9.7116" x2="-1.5254" y2="-8.2468" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2674"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
<linearGradient id="paint5_linear_12991_9775" x1="20.75" y1="-9.7116" x2="-1.5254" y2="-8.2468" gradientUnits="userSpaceOnUse">
<stop stop-color="#FF2672"/>
<stop offset="1" stop-color="#FF6B3C"/>
</linearGradient>
</defs>
</svg></div>
<div class="custom-nav-wrapper">
        <button class="custom-menu-btn"><span class="custom-menu-text" style="color: white;">Settings</span></button>

      </div>
    `
    );
  }
  const moveNav = () => {
    const elementToMove = document.querySelector(".topmenu-nav");
    const targetElement = document.querySelector("section.w-full");

    if (elementToMove && targetElement) {
      targetElement.prepend(elementToMove);
      return true;
    }
    return false;
  };

  if (!moveNav()) {
    const moveLinksObserver = new MutationObserver((_, obs) => {
      if (moveNav()) {
        obs.disconnect();
      }
    });

    moveLinksObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

const backButtonObserver = new MutationObserver((_, obs) => {
  const oldButton = document.getElementById("backButtonv2");
  if (oldButton) {
    let currentPath = window.location.pathname;
    let locationId = currentPath.split(`/v2/location/`)[1];
    locationId = locationId.split("/")[0];
    const newButton = document.createElement("a");
    Array.from(oldButton.attributes).forEach(attr => {
      newButton.setAttribute(attr.name, attr.value);
    });
    newButton.href = `/v2/location/${locationId}/dashboard`;
    newButton.innerHTML = oldButton.innerHTML;
    oldButton.parentNode.replaceChild(newButton, oldButton);
    obs.disconnect();
  }
});

backButtonObserver.observe(document.body, { childList: true, subtree: true });

const settingsButton = new MutationObserver((_, obs) => {
  const oldButton = document.getElementById("sb_settings");
  if (oldButton) {
    const newButton = document.createElement("a");
    Array.from(oldButton.attributes).forEach(attr => {
      newButton.setAttribute(attr.name, attr.value);
    });
    newButton.innerHTML = oldButton.innerHTML;
    oldButton.parentNode.replaceChild(newButton, oldButton);
    obs.disconnect();
  }
});

settingsButton.observe(document.body, { childList: true, subtree: true });

// 2. Introduce a state flag to track if we've already run the function
let isCurrentlyTargetPage = false;

// 3. Create the MutationObserver
const settingsTopNavObserver = new MutationObserver(mutationsList => {
  for (const mutation of mutationsList) {
    if (mutation.type === "attributes" && mutation.attributeName === "class") {
      const body = document.body;
      const hasTargetClass = body.classList.contains("is-settings-page-custom") || body.classList.contains("isAdmin");

      // 4. Only run if it HAS the class, AND we haven't already marked it as true
      if (hasTargetClass && !isCurrentlyTargetPage) {
        isCurrentlyTargetPage = true; // Update our flag so it doesn't run again
        handleSettingsTopNav();
      }
      // 5. Optional: Reset the flag if the classes are removed later
      else if (!hasTargetClass && isCurrentlyTargetPage) {
        isCurrentlyTargetPage = false;
      }
    }
  }
});

// 6. Start observing the body
settingsTopNavObserver.observe(document.body, {
  attributes: true,
  attributeFilter: ["class"]
});

// 7. Safety Check: Run immediately on load
const initialCheck = document.body.classList.contains("is-settings-page-custom") || document.body.classList.contains("isAdmin");

if (initialCheck) {
  isCurrentlyTargetPage = true; // Set the flag immediately
  handleSettingsTopNav();
}

const applySidebarWrapper = () => {
  if (!document.body.classList.contains("is-settings-page-custom")) return;

  const target = document.getElementById("location-switcher-sidbar-v2");
  if (target && !target.parentElement.classList.contains("search-dropdown-sidebar")) {
    const sibling = target.nextElementSibling;
    if (!sibling) return;

    const wrapper = document.createElement("div");
    wrapper.className = "search-dropdown-sidebar";

    target.parentNode.insertBefore(wrapper, target);
    wrapper.appendChild(target);
    wrapper.appendChild(sibling);
  }
};

const sidebarLayoutWatcher = new MutationObserver(() => {
  applySidebarWrapper();
});

sidebarLayoutWatcher.observe(document.body, {
  childList: true,
  subtree: true
});

applySidebarWrapper();

const targetNode = document.body;
const config = { childList: true, subtree: true };

// const callback = function (mutationsList, observer) {
//   const el = document.querySelector(".single_location");
//   if (el) {
//     el.classList.remove("single_location");
//     el.classList.add("single-location-wrapper-custom");
//   }
// };

// const scanner = new MutationObserver(callback);
// scanner.observe(targetNode, config);

const targetId = "hl_header--help-icon";

const styleCleaner = new MutationObserver(() => {
  const element = document.getElementById(targetId);
  if (element && element.style.backgroundColor !== "transparent") {
    element.style.setProperty("background", "transparent", "important");
    element.style.setProperty("background-color", "transparent", "important");
  }
});

styleCleaner.observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ["style"]
});

const purpleLock = () => {
  const avatars = document.querySelectorAll(".active-call-avatar");
  const targetColor = "rgb(80, 58, 243)";

  avatars.forEach(el => {
    if (window.getComputedStyle(el).backgroundColor !== targetColor) {
      domWatcher.disconnect();

      el.style.setProperty("background-color", targetColor, "important");
      startWatching();
    }
  });
};

const domWatcher = new MutationObserver(purpleLock);

const startWatching = () => {
  domWatcher.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style"]
  });
};

purpleLock();
startWatching();

const bodyClassHandler = () => {
  const body = document.body;
  const currentPath = window.location.pathname;

  const agencyPaths = [
    "/saas_dashboard",
    "/prospecting",
    "/sub-accounts",
    "/snapshots",
    "/reselling",
    "/marketplace",
    "/affiliate_portal/dashboard",
    "/template-library-admin",
    "/partners",
    "/university",
    "/saas_education",
    "/mobile-app-customiser",
    "/integration",
    "/agency_dashboard"
  ];

  const isAgencyView = agencyPaths.some(path => currentPath.endsWith(path));

  if (isAgencyView) {
    if (!body.classList.contains("agency-dash-view")) {
      body.classList.add("agency-dash-view");
    }
  } else {
    if (body.classList.contains("agency-dash-view")) {
      body.classList.remove("agency-dash-view");
    }
  }
};

bodyClassHandler();

window.addEventListener("locationchange", bodyClassHandler);
window.addEventListener("ghl-theme:route-change", bodyClassHandler);

const urlStateWatcher = new MutationObserver(() => {
  bodyClassHandler();
});

urlStateWatcher.observe(document.body, {
  childList: true,
  subtree: true
});

(function () {
  const sync = () => {
    const path = window.location.pathname.split("/");
    const idx = path.indexOf("location");

    if (idx !== -1 && path[idx + 1]) {
      const id = path[idx + 1];

      const backBtn = document.getElementById("backButtonv2");
      if (backBtn) {
        const backTarget = `/v2/location/${id}/dashboard`;
        if (backBtn.getAttribute("href") !== backTarget) {
          backBtn.setAttribute("href", backTarget);
        }
      }

      const settingsBtn = document.getElementById("sb_settings");
      if (settingsBtn) {
        const settingsTarget = `/v2/location/${id}/settings/company`;
        if (settingsBtn.getAttribute("href") !== settingsTarget) {
          settingsBtn.setAttribute("href", settingsTarget);
        }
      }
    }
  };

  window.addEventListener("popstate", sync);
  window.addEventListener("locationchange", sync);
  window.addEventListener("ghl-theme:route-change", sync);

  const watch = new MutationObserver(() => {
    sync();
  });

  watch.observe(document.body, {
    childList: true,
    subtree: true
  });

  document.addEventListener("click", e => {
    if (e.target.closest(".hl_account") || e.target.closest(".hl_location-recent")) {
      setTimeout(sync, 150);
    }
  });

  sync();
})();

(function () {
  const relocateSettings = () => {
    const headerNav = document.querySelector('div.flex.flex-col.w-full.overflow-x-hidden.overflow-y-auto.hl_nav-header > nav.flex-1.w-full[aria-label="header"]');
    const settingsLink = document.querySelector(
      'div.flex.flex-col.absolute.bottom-3\\.5.w-full.hl_nav-settings > nav.flex-1.w-full[aria-label="footer"] > a#sb_agency-settings[meta="agency-settings"]'
    );

    if (headerNav && settingsLink) {
      headerNav.appendChild(settingsLink);
    }
  };

  const domWatcher = new MutationObserver(relocateSettings);

  domWatcher.observe(document.body, {
    childList: true,
    subtree: true
  });

  relocateSettings();
})();

(function () {
  const fixSocialPlannerNav = () => {
    const isSocialPlannerSettings = window.location.pathname.includes("/marketing/social-planner");
    if (!isSocialPlannerSettings) return;

    const displacedNav = document.querySelector("section.w-full > .topmenu-nav");
    const targetHeaderArea = document.querySelector(".hl_header .hl-top-left");

    if (displacedNav && targetHeaderArea) {
      const existingNavInHeader = targetHeaderArea.querySelector(".topmenu-nav");

      if (!existingNavInHeader) {
        targetHeaderArea.appendChild(displacedNav);
      } else {
        displacedNav.remove();
      }
    }
  };

  let socialPlannerObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.classList && node.classList.contains("topmenu-nav")) {
              fixSocialPlannerNav();
            } else if (node.querySelector && node.querySelector(".topmenu-nav")) {
              fixSocialPlannerNav();
            }
          }
        });
      }
    }
  });

  socialPlannerObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  window.addEventListener("locationchange", fixSocialPlannerNav);
  window.addEventListener("ghl-theme:route-change", fixSocialPlannerNav);

  setTimeout(fixSocialPlannerNav, 500);
})();

// const watchRoute = new MutationObserver(() => {
//   const path = window.location.pathname;
//   const excluded = "/marketing/social-planner/settings";
//   const body = document.body;
//   const targetClass = "sub-setting-page";

//   if (path.includes("/settings") && path !== excluded) {
//     if (!body.classList.contains(targetClass)) {
//       body.classList.add(targetClass);
//     }
//   } else {
//     body.classList.remove(targetClass);
//   }
// });

// watchRoute.observe(document.documentElement, { subtree: true, childList: true });

(function () {
  const targetClass = "automation-campaigns-page";

  const handleCampaignsPageClass = () => {
    const body = document.body;
    const isCampaignsPage = window.location.pathname.includes("/crm-settings");

    if (isCampaignsPage) {
      if (!body.classList.contains(targetClass)) {
        body.classList.add(targetClass);
      }
    } else {
      if (body.classList.contains(targetClass)) {
        body.classList.remove(targetClass);
      }
    }
  };

  handleCampaignsPageClass();

  window.addEventListener("locationchange", handleCampaignsPageClass);
  window.addEventListener("ghl-theme:route-change", handleCampaignsPageClass);

  const campaignRouteWatcher = new MutationObserver(() => {
    handleCampaignsPageClass();
  });

  campaignRouteWatcher.observe(document.body, {
    childList: true,
    subtree: true
  });
})();

(function () {
  const purple = "var(--Dark-Purple)";
  const selector = ".opportunitiesCard.stageHeaderBg.crm-opportunities-stage-header.ui-card";

  let isDragging = false;
  let interval;

  const fixHeaders = () => {
    if (!isDragging) return;

    document.querySelectorAll(selector).forEach(card => {
      card.style.setProperty("--68ac5946", purple, "important");
      card.style.setProperty("background", purple, "important");
      card.style.setProperty("background-color", purple, "important");
      card.style.setProperty("border-radius", "10px", "important");
    });
  };

  document.addEventListener("mousedown", e => {
    if (!e.target.closest(".opportunitiesCard")) return;

    isDragging = true;
    interval = setInterval(fixHeaders, 25);
  });

  document.addEventListener("mouseup", () => {
    isDragging = false;
    clearInterval(interval);

    document.querySelectorAll(selector).forEach(card => {
      card.style.removeProperty("background");
      card.style.removeProperty("background-color");
      card.style.removeProperty("border-radius");
    });
  });
})();

setTimeout(() => {
  const addCompanySettingsIcon = () => {
    const sidebarWrapper = document.querySelector(".sidebar-v2-location");
    const navHeader = document.querySelector(".hl_nav-header");
    const existingLink = document.querySelector("#custom-company-settings-link");

    if (!sidebarWrapper || !navHeader) return;

    if (!sidebarWrapper.classList.contains("v2-collapse")) {
      if (existingLink) existingLink.remove();
      return;
    }

    if (existingLink && existingLink.parentElement !== navHeader) {
      navHeader.appendChild(existingLink);
      return;
    }

    if (existingLink) return;

    const locationMatch = window.location.pathname.match(/\/v2\/location\/([^/]+)/);
    const locationId = locationMatch ? locationMatch[1] : "";
    const href = locationId ? `/v2/location/${locationId}/settings/company` : "/settings/company";

    const companyLink = document.createElement("a");

    companyLink.id = "custom-company-settings-link";
    companyLink.href = href;
    companyLink.className = "flex justify-center mb-4 px-3 flex-shrink-0";
    companyLink.style.position = "relative";

    companyLink.innerHTML = `
        <svg width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.3388 3.18889C17.3582 2.85088 17.2453 2.51049 16.9807 2.29929C16.2094 1.6837 15.3451 1.17988 14.4123 0.812312C14.0967 0.687953 13.7447 0.760535 13.4612 0.946903C12.6593 1.4742 11.7346 1.78774 10.75 1.78774C9.76544 1.78774 8.8407 1.4742 8.03876 0.946903C7.75532 0.760535 7.40328 0.687953 7.08768 0.812312C6.15483 1.17989 5.29052 1.68374 4.51923 2.29936C4.25463 2.51055 4.14168 2.85093 4.1611 3.18892C4.21617 4.147 4.02508 5.10457 3.53307 5.95733C3.041 6.81022 2.30768 7.45467 1.45083 7.886C1.14773 8.03858 0.909019 8.30747 0.859163 8.64313C0.787256 9.12726 0.75 9.6227 0.75 10.1268C0.75 10.631 0.787259 11.1265 0.859172 11.6106C0.909029 11.9463 1.14773 12.2152 1.45084 12.3677C2.30769 12.7991 3.041 13.4435 3.53307 14.2964C4.02507 15.1492 4.21616 16.1067 4.1611 17.0648C4.14168 17.4028 4.25463 17.7431 4.51923 17.9543C5.2905 18.5699 6.15478 19.0738 7.08758 19.4413C7.4032 19.5657 7.75526 19.4931 8.0387 19.3067C8.84065 18.7794 9.76541 18.4658 10.75 18.4658C11.7346 18.4658 12.6594 18.7794 13.4613 19.3067C13.7447 19.4931 14.0968 19.5657 14.4124 19.4413C15.3452 19.0738 16.2094 18.57 16.9807 17.9544C17.2453 17.7432 17.3582 17.4028 17.3388 17.0648C17.2837 16.1067 17.4748 15.1492 17.9668 14.2964C18.4589 13.4435 19.1922 12.799 20.0491 12.3677C20.3523 12.2151 20.591 11.9463 20.6408 11.6106C20.7127 11.1264 20.75 10.631 20.75 10.1268C20.75 9.62276 20.7128 9.12739 20.6409 8.64333C20.591 8.30765 20.3523 8.03874 20.0492 7.88616C19.1923 7.45485 18.4589 6.81039 17.9668 5.95746C17.4748 5.10466 17.2837 4.14703 17.3388 3.18889Z" stroke="url(#company_icon_gradient_1)" stroke-width="1.5" stroke-linejoin="round"></path>
          <path d="M14.0833 10.1261C14.0833 11.9684 12.5909 13.4618 10.75 13.4618C8.90905 13.4618 7.41667 11.9684 7.41667 10.1261C7.41667 8.28391 8.90905 6.7905 10.75 6.7905C12.5909 6.7905 14.0833 8.28391 14.0833 10.1261Z" stroke="url(#company_icon_gradient_2)" stroke-width="1.5" stroke-linejoin="round"></path>
          <defs>
            <linearGradient id="company_icon_gradient_1" x1="21.7318" y1="0.869796" x2="-0.215198" y2="6.89941" gradientUnits="userSpaceOnUse">
              <stop stop-color="#EA1877"></stop>
              <stop offset="1" stop-color="#FF8732"></stop>
            </linearGradient>
            <linearGradient id="company_icon_gradient_2" x1="21.7318" y1="0.869796" x2="-0.215198" y2="6.89941" gradientUnits="userSpaceOnUse">
              <stop stop-color="#EA1877"></stop>
              <stop offset="1" stop-color="#FF8732"></stop>
            </linearGradient>
          </defs>
        </svg>
      `;

    navHeader.appendChild(companyLink);
  };

  addCompanySettingsIcon();

  new MutationObserver(addCompanySettingsIcon).observe(document.body, {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: ["class"]
  });
}, 1000);


function installDelayedTasksSvgShow() {
  if (window.__delayedTasksSvgShowInstalled) return;
  window.__delayedTasksSvgShowInstalled = true;

  let timer = null;

  const hideTasksSvg = () => {
    if (!window.location.pathname.includes("/tasks")) return;

    const iconContainer = document.getElementById("hl-active-icon-container");
    if (!iconContainer) return;

    const svg = iconContainer.querySelector("svg");
    if (!svg) return;

    svg.style.setProperty("display", "none", "important");

    clearTimeout(timer);

    timer = setTimeout(() => {
      if (!window.location.pathname.includes("/tasks")) return;

      const currentIconContainer = document.getElementById("hl-active-icon-container");
      if (!currentIconContainer) return;

      const currentSvg = currentIconContainer.querySelector("svg");
      if (!currentSvg) return;

      currentSvg.style.removeProperty("display");
    }, 2000);
  };

  window.addEventListener("ghl-theme:route-change", hideTasksSvg);
  window.addEventListener("locationchange", hideTasksSvg);
  window.addEventListener("popstate", hideTasksSvg);

  hideTasksSvg();
}

installDelayedTasksSvgShow();

const syncTasksSidebarCounter = () => {
  const locationId = window.location.pathname.split("/")[3];
  const tasksLink = document.querySelector('#sidebar-v2 a[data-href*="/tasks"], #sidebar-v2 a[href*="/tasks"]');

  if (!locationId || !tasksLink) return;

  const storedTaskCountKey = `ghl-task-count:${locationId}`;
  const taskCountText = document.querySelector(".pipeline-ribbon .left-box .count")?.textContent.trim();
  const taskCountMatch = taskCountText?.match(/^(\d+)\s+Tasks?$/i);
  const taskCount = taskCountMatch ? taskCountMatch[1] : sessionStorage.getItem(storedTaskCountKey);

  if (!taskCount) return;

  if (taskCountMatch) {
    sessionStorage.setItem(storedTaskCountKey, taskCount);
  }

  let taskCounter = tasksLink.querySelector(":scope > #taskCount");

  if (!taskCounter) {
    taskCounter = document.createElement("span");
    taskCounter.id = "taskCount";
    tasksLink.appendChild(taskCounter);
  }

  taskCounter.textContent = taskCount;
};

registerSpaTask("sync-tasks-sidebar-counter", syncTasksSidebarCounter, {
  intervalMs: 300,
  minIntervalMs: 50
});

/**
 * Fix Tasks Title Overrides
 * Synchronizes the top menu navbar title with 'Tasks' when on the tasks view
 */
(function () {
  const fixTopMenuNavTitle = () => {
    if (window.location.pathname.includes("/tasks")) {
      // 1. Target the native SPA top navigation title element
      const nativeTitle = document.querySelector(".hl-top-left .topmenu-navtitle");
      if (nativeTitle && nativeTitle.textContent.trim() !== "Tasks") {
        nativeTitle.textContent = "Tasks";
      }

      // 2. Target your custom injected floating menu wrapper text fallback
      const customMenuBtnText = document.querySelector(".custom-nav-wrapper .custom-menu-text");
      if (customMenuBtnText && customMenuBtnText.innerText.trim() !== "Tasks") {
        // customMenuBtnText.innerText = "Tasks";
      }
    }
  };

  // Register with your existing robust batched SPA lifecycle helper
  if (window.__ghlThemeLifecycle) {
    window.__ghlThemeLifecycle.register("tasks-title-sync", fixTopMenuNavTitle, {
      intervalMs: 300,
      minIntervalMs: 50
    });
  } else {
    // Fallback engine if processed before the lifecycle API boots
    setInterval(fixTopMenuNavTitle, 300);
  }
})();

(() => {
  const cleanEditDashboardButtons = () => {
    document.querySelectorAll(".flex.items-center.flex-wrap.gap-2").forEach((wrapper) => {
      const buttons = wrapper.querySelectorAll('button[id="location-dashboard_btn--edit-dashboard"]');

      buttons.forEach((button, index) => {
        if (index !== buttons.length - 1) button.remove();
      });
    });
  };

  cleanEditDashboardButtons();

  new MutationObserver(cleanEditDashboardButtons).observe(document.body, {
    childList: true,
    subtree: true
  });

  ["pushState", "replaceState"].forEach((type) => {
    const original = history[type];

    history[type] = function () {
      const result = original.apply(this, arguments);
      cleanEditDashboardButtons();
      return result;
    };
  });

  window.addEventListener("popstate", cleanEditDashboardButtons);
})();
